// src/pages/Admins.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signOut, updatePassword, EmailAuthProvider, reauthenticateWithCredential, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc, updateDoc, collection, getDocs, query, where, deleteDoc } from 'firebase/firestore';
import { db } from '../components/firebase-config.js';
import { FaUserMd, FaUserInjured, FaSignOutAlt, FaEdit, FaEye, FaEyeSlash, FaCog, FaHome, FaShieldAlt, FaUserCog, FaChartBar, FaBell, FaClipboard, FaTrashAlt, FaRedoAlt } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import pptxgen from 'pptxgenjs';
import { Document, Packer, Paragraph, Table, TableCell, TableRow, TextRun, HeadingLevel, AlignmentType, WidthType } from 'docx';
import html2canvas from 'html2canvas';

const Admins = () => {
  const [loading, setLoading] = useState(true);
  const [structure, setStructure] = useState(null);
  const [stats, setStats] = useState({
    patients: 0,
    medecins: 0
  });
  const [admin, setAdmin] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    site: '',
    telephone: '',
    responsable: '',
    specialites: [],
    assurances: [],
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    adminNom: '',
    adminPrenom: '',
    adminTelephone: '',
    adminCurrentPassword: '',
    adminNewPassword: '',
    adminConfirmPassword: '',
    adminEmailNotifications: false,
    adminNewPatientAlert: false,
    adminLoginAlert: false
  });

  // Ajoutez un nouveau state pour gérer l'affichage du menu mobile
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Ajoutez ces données de démonstration dans le composant Admins (juste après les déclarations de state)
  const [patientData, setPatientData] = useState([
    { name: 'Jan', count: 12 },
    { name: 'Fév', count: 19 },
    { name: 'Mar', count: 25 },
    { name: 'Avr', count: 34 },
    { name: 'Mai', count: 40 },
    { name: 'Juin', count: 45 },
    { name: 'Juil', count: stats.patients }
  ]);

  const [medecinData, setMedecinData] = useState([
    { name: 'Ophtalmologie', count: 4 },
    { name: 'Rétinologie', count: 3 },
    { name: 'Cornée', count: 5 },
    { name: 'Glaucome', count: 2 },
    { name: 'Pédiatrie', count: 3 }
  ]);

  // 1. Ajouter un nouveau state pour les finances
  const [medecinsFinance, setMedecinsFinance] = useState([]);
  const [revenusData, setRevenusData] = useState([]);
  const [periodeRevenu, setPeriodeRevenu] = useState('mois');
  const [revenuTotal, setRevenuTotal] = useState(0);

  // Ajouter ces nouveaux states pour la pagination et la recherche
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage] = useState(10);

  // Ajouter ces nouveaux states
  const [selectedMedecinId, setSelectedMedecinId] = useState('');
  const [medecinRapport, setMedecinRapport] = useState({
    rdvCount: 0,
    revenuTotal: 0,
    periodeData: []
  });

  // Ajouter un nouveau state pour la période du rapport médecin
  const [periodeMedecinRapport, setPeriodeMedecinRapport] = useState('mois');

  // Ajout d'un nouveau state pour stocker les médecins avec leurs rendez-vous
  const [medecinRendezvous, setMedecinRendezvous] = useState([]);
  const [periodeMedecinsRdv, setPeriodeMedecinsRdv] = useState('mois'); // Période par défaut

  // Ajouter de nouveaux states pour les historiques
  const [historiqueRevenus, setHistoriqueRevenus] = useState({
    jour: {},
    semaine: {},
    mois: {},
    annee: {}
  });

  // Ajouter un state pour le médecin sélectionné pour afficher le détail
  const [selectedMedecinForDetail, setSelectedMedecinForDetail] = useState(null);
  const [detailsRendezvousMedecin, setDetailsRendezvousMedecin] = useState([]);

  // Ajouter un state pour détecter si l'appareil est un smartphone
  const [isMobile, setIsMobile] = useState(false);

  // Ajouter un useEffect pour détecter le type d'appareil au chargement et lors du redimensionnement
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Vérifier au chargement initial
    checkIfMobile();
    
    // Ajouter un écouteur d'événement pour le redimensionnement
    window.addEventListener('resize', checkIfMobile);
    
    // Nettoyer l'écouteur lors du démontage
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Fonction pour récupérer les médecins avec leurs rendez-vous
  const fetchMedecinsRendezvous = async (structureId) => {
    try {
      // Récupérer l'historique du localStorage
      try {
        const historiqueStocke = localStorage.getItem(`historique_revenus_${structureId}`);
        if (historiqueStocke) {
          setHistoriqueRevenus(JSON.parse(historiqueStocke));
        }
      } catch (e) {
        console.error("Erreur lors de la récupération de l'historique:", e);
      }
      
      // Reste de la fonction inchangé jusqu'au calcul des revenus
      const medecinsQuery = query(
        collection(db, 'medecins'), 
        where("structureId", "==", structureId)
      );
      const medecinsSnapshot = await getDocs(medecinsQuery);
      
      const rdvsQuery = query(
        collection(db, 'rendezvous'), 
        where("structureId", "==", structureId)
      );
      const rdvsSnapshot = await getDocs(rdvsQuery);
      
      const rdvCountByMedecin = {};
      const rdvsByMedecin = {};
      
      medecinsSnapshot.forEach(doc => {
        rdvsByMedecin[doc.id] = [];
      });
      
      rdvsSnapshot.forEach(doc => {
        const rdv = doc.data();
        if (rdv.medecinId && rdvsByMedecin[rdv.medecinId]) {
          rdvsByMedecin[rdv.medecinId].push({
            id: doc.id,
            ...rdv
          });
          rdvCountByMedecin[rdv.medecinId] = (rdvCountByMedecin[rdv.medecinId] || 0) + 1;
        }
      });
      
      const medecinsAvecRdv = [];
      medecinsSnapshot.forEach(doc => {
        const medecinData = doc.data();
        const tarif = medecinData.tarif || 0;
        
        medecinsAvecRdv.push({
          id: doc.id,
          nom: medecinData.nom || '',
          prenom: medecinData.prenom || '',
          specialite: medecinData.specialite || '',
          tarif: tarif,
          nbRendezvous: rdvCountByMedecin[doc.id] || 0,
          rdvs: rdvsByMedecin[doc.id] || []
        });
      });
      
      // Utiliser la fonction mise à jour pour calculer les revenus
      const medecinsAvecRevenus = calculerRevenusMedecins(medecinsAvecRdv, periodeMedecinsRdv);
      
      // Trier par nombre de rendez-vous
      medecinsAvecRevenus.sort((a, b) => b.nbRendezvous - a.nbRendezvous);
      
      setMedecinRendezvous(medecinsAvecRevenus);
    } catch (error) {
      console.error("Erreur lors de la récupération des rendez-vous des médecins:", error);
      toast.error("Erreur lors de la récupération des données");
    }
  };

  // Ajouter une fonction pour calculer les revenus par période
  const calculerRevenusMedecins = (medecins, periode, reinitialiser = false) => {
    const now = new Date();
    
    // Clés pour identifier les périodes actuelles
    const jourId = now.toISOString().split('T')[0]; // format YYYY-MM-DD
    const semaineId = `S${getWeekNumber(now)}-${now.getFullYear()}`; // format S12-2023
    const moisId = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`; // format 2023-05
    const anneeId = now.getFullYear().toString(); // format 2023
    
    // Définir les dates limites pour chaque période
    const jourDebut = new Date(now);
    jourDebut.setHours(0, 0, 0, 0);
    
    const semaineDebut = new Date(now);
    semaineDebut.setDate(now.getDate() - now.getDay());
    semaineDebut.setHours(0, 0, 0, 0);
    
    const moisDebut = new Date(now);
    moisDebut.setDate(1);
    moisDebut.setHours(0, 0, 0, 0);
    
    const anneeDebut = new Date(now);
    anneeDebut.setMonth(0, 1);
    anneeDebut.setHours(0, 0, 0, 0);
    
    // Copier l'historique actuel ou le réinitialiser si demandé
    const nouvelHistorique = reinitialiser 
      ? { jour: {}, semaine: {}, mois: {}, annee: {} } 
      : {...historiqueRevenus};
    
    // Calculer les revenus pour chaque médecin
    const medecinsAvecRevenus = medecins.map(medecin => {
      // Calculer les revenus pour la période actuelle
      let revenuJour = 0;
      let revenuSemaine = 0;
      let revenuMois = 0;
      let revenuAnnee = 0;
      
      medecin.rdvs.forEach(rdv => {
        if (rdv.date) {
          const rdvDate = rdv.date.toDate ? rdv.date.toDate() : new Date(rdv.date);
          
          // Calculer pour chaque période
          if (rdvDate >= jourDebut) {
            revenuJour += medecin.tarif;
          }
          
          if (rdvDate >= semaineDebut) {
            revenuSemaine += medecin.tarif;
          }
          
          if (rdvDate >= moisDebut) {
            revenuMois += medecin.tarif;
          }
          
          if (rdvDate >= anneeDebut) {
            revenuAnnee += medecin.tarif;
          }
        }
      });
      
      // Mettre à jour l'historique pour ce médecin
      if (!reinitialiser) {
        // Jour
        if (!nouvelHistorique.jour[jourId]) {
          nouvelHistorique.jour[jourId] = {};
        }
        if (!nouvelHistorique.jour[jourId][medecin.id]) {
          nouvelHistorique.jour[jourId][medecin.id] = 0;
        }
        nouvelHistorique.jour[jourId][medecin.id] += revenuJour;
        
        // Semaine
        if (!nouvelHistorique.semaine[semaineId]) {
          nouvelHistorique.semaine[semaineId] = {};
        }
        if (!nouvelHistorique.semaine[semaineId][medecin.id]) {
          nouvelHistorique.semaine[semaineId][medecin.id] = 0;
        }
        nouvelHistorique.semaine[semaineId][medecin.id] += revenuSemaine;
        
        // Mois
        if (!nouvelHistorique.mois[moisId]) {
          nouvelHistorique.mois[moisId] = {};
        }
        if (!nouvelHistorique.mois[moisId][medecin.id]) {
          nouvelHistorique.mois[moisId][medecin.id] = 0;
        }
        nouvelHistorique.mois[moisId][medecin.id] += revenuMois;
        
        // Année
        if (!nouvelHistorique.annee[anneeId]) {
          nouvelHistorique.annee[anneeId] = {};
        }
        if (!nouvelHistorique.annee[anneeId][medecin.id]) {
          nouvelHistorique.annee[anneeId][medecin.id] = 0;
        }
        nouvelHistorique.annee[anneeId][medecin.id] += revenuAnnee;
      }
      
      // Calculer les totaux historiques pour ce médecin
      let historiqueJour = 0;
      let historiqueSemaine = 0;
      let historiqueMois = 0;
      let historiqueAnnee = 0;
      
      // Additionner tous les revenus de l'historique
      Object.keys(nouvelHistorique.jour).forEach(jour => {
        historiqueJour += nouvelHistorique.jour[jour][medecin.id] || 0;
      });
      
      Object.keys(nouvelHistorique.semaine).forEach(semaine => {
        historiqueSemaine += nouvelHistorique.semaine[semaine][medecin.id] || 0;
      });
      
      Object.keys(nouvelHistorique.mois).forEach(mois => {
        historiqueMois += nouvelHistorique.mois[mois][medecin.id] || 0;
      });
      
      Object.keys(nouvelHistorique.annee).forEach(annee => {
        historiqueAnnee += nouvelHistorique.annee[annee][medecin.id] || 0;
      });
      
      // Retourner le médecin avec ses revenus historiques
      return {
        ...medecin,
        revenuJour,
        revenuSemaine,
        revenuMois,
        revenuAnnee,
        historiqueJour,
        historiqueSemaine,
        historiqueMois,
        historiqueAnnee
      };
    });
    
    // Mettre à jour l'historique global
    setHistoriqueRevenus(nouvelHistorique);
    
    // Sauvegarder l'historique dans localStorage pour persistance
    try {
      localStorage.setItem(`historique_revenus_${admin?.structureId}`, JSON.stringify(nouvelHistorique));
    } catch (error) {
      console.error("Erreur lors de la sauvegarde de l'historique:", error);
    }
    
    return medecinsAvecRevenus;
  };

  // Ajouter une fonction utilitaire pour obtenir le numéro de semaine
  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  // Ajouter une fonction pour mettre à jour le tarif d'un médecin
  const handleTarifMedecinRendezvous = async (medecinId, nouveauTarif) => {
    try {
      setLoading(true);
      
      const tarifNumber = parseFloat(nouveauTarif);
      if (isNaN(tarifNumber)) {
        toast.error("Le tarif doit être un nombre valide");
        return;
      }
      
      await updateDoc(doc(db, 'medecins', medecinId), {
        tarif: tarifNumber
      });
      
      const nouveauxMedecins = medecinRendezvous.map(medecin => {
        if (medecin.id === medecinId) {
          return {
            ...medecin,
            tarif: tarifNumber
          };
        }
        return medecin;
      });
      
      // Recalculer les revenus avec l'historique
      const medecinsAvecRevenus = calculerRevenusMedecins(nouveauxMedecins, periodeMedecinsRdv);
      
      setMedecinRendezvous(medecinsAvecRevenus);
      
      toast.success("Tarif mis à jour avec succès");
    } catch (error) {
      console.error("Erreur lors de la mise à jour du tarif:", error);
      toast.error("Erreur lors de la mise à jour du tarif");
    } finally {
      setLoading(false);
    }
  };

  // Ajouter une fonction pour changer la période d'affichage
  const handlePeriodeMedecinsRdvChange = (nouvellePeriode) => {
    setPeriodeMedecinsRdv(nouvellePeriode);
    calculerRevenusMedecins(medecinRendezvous, nouvellePeriode);
  };

  // Fonction améliorée pour filtrer et paginer les médecins
  const paginatedMedecins = () => {
    // Si aucun terme de recherche, renvoyer tous les médecins (paginés)
    if (!searchTerm.trim()) {
      const indexOfLastItem = currentPage * itemsPerPage;
      const indexOfFirstItem = indexOfLastItem - itemsPerPage;
      return medecinsFinance.slice(indexOfFirstItem, indexOfLastItem);
    }
    
    // Normaliser le terme de recherche (supprimer les accents, mettre en minuscule)
    const normalizedSearchTerm = searchTerm.toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    
    // Filtrer avec une recherche plus étendue
    const filtered = medecinsFinance.filter(medecin => {
      // Normaliser les valeurs du médecin
      const nomNormalized = medecin.nom.toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const prenomNormalized = medecin.prenom.toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const specialiteNormalized = (medecin.specialite || "").toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      
      // Créer un nom complet pour la recherche
      const nomComplet = `${prenomNormalized} ${nomNormalized}`;
      const nomCompletInverse = `${nomNormalized} ${prenomNormalized}`;
      
      // Recherche par mots-clés (séparés par des espaces)
      const searchTerms = normalizedSearchTerm.split(/\s+/);
      
      // Vérifier si tous les termes de recherche sont présents dans au moins un des champs
      return searchTerms.every(term => 
        nomNormalized.includes(term) || 
        prenomNormalized.includes(term) || 
        specialiteNormalized.includes(term) ||
        nomComplet.includes(term) ||
        nomCompletInverse.includes(term) ||
        // Recherche par initiales (si le terme est court)
        (term.length <= 2 && 
          (prenomNormalized.startsWith(term) || nomNormalized.startsWith(term)))
      );
    });
    
    // Calculer les indices pour la pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    
    // Retourner les résultats paginés
    return filtered.slice(indexOfFirstItem, indexOfLastItem);
  };

  // Fonction pour calculer le nombre total de pages
  const totalPages = () => {
    // Si aucun terme de recherche, utiliser le nombre total de médecins
    if (!searchTerm.trim()) {
      return Math.ceil(medecinsFinance.length / itemsPerPage);
    }
    
    // Normaliser le terme de recherche
    const normalizedSearchTerm = searchTerm.toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    
    // Filtrer avec la même logique que paginatedMedecins
    const filtered = medecinsFinance.filter(medecin => {
      const nomNormalized = medecin.nom.toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const prenomNormalized = medecin.prenom.toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const specialiteNormalized = (medecin.specialite || "").toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      
      const nomComplet = `${prenomNormalized} ${nomNormalized}`;
      const nomCompletInverse = `${nomNormalized} ${prenomNormalized}`;
      
      const searchTerms = normalizedSearchTerm.split(/\s+/);
      
      return searchTerms.every(term => 
        nomNormalized.includes(term) || 
        prenomNormalized.includes(term) || 
        specialiteNormalized.includes(term) ||
        nomComplet.includes(term) ||
        nomCompletInverse.includes(term) ||
        (term.length <= 2 && 
          (prenomNormalized.startsWith(term) || nomNormalized.startsWith(term)))
      );
    });
    
    return Math.ceil(filtered.length / itemsPerPage);
  };

  // Effet pour vérifier l'authentification et charger les données
  useEffect(() => {
    const fetchData = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) {
          toast.error("Vous devez être connecté pour accéder à cette page");
          navigate('/');
          return;
        }

        // Vérifier si l'utilisateur est un admin
        const adminDoc = await getDoc(doc(db, 'admins', user.uid));
        if (!adminDoc.exists()) {
          toast.error("Vous n'avez pas les droits d'administrateur");
          signOut(auth);
          navigate('/');
          return;
        }

        const adminData = adminDoc.data();
        setAdmin(adminData);

        // Récupérer les informations de la structure
        const structureDoc = await getDoc(doc(db, 'structures', adminData.structureId));
        if (!structureDoc.exists()) {
          toast.error("Structure non trouvée");
          return;
        }

        const structureData = structureDoc.data();
        setStructure(structureData);
        setFormData({
          nom: structureData.nom || '',
          email: structureData.email || '',
          site: structureData.site || '',
          telephone: structureData.telephone || '',
          responsable: structureData.responsable || '',
          specialites: Array.isArray(structureData.specialites) ? structureData.specialites : [],
          assurances: Array.isArray(structureData.assurances) ? structureData.assurances : [],
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
          adminNom: adminData.nom || '',
          adminPrenom: adminData.prenom || '',
          adminTelephone: adminData.telephone || '',
          adminCurrentPassword: '',
          adminNewPassword: '',
          adminConfirmPassword: '',
          adminEmailNotifications: adminData.adminEmailNotifications || false,
          adminNewPatientAlert: adminData.adminNewPatientAlert || false,
          adminLoginAlert: adminData.adminLoginAlert || false
        });

        // Récupérer les statistiques
        await fetchStats(adminData.structureId);

        // Ajouter ceci à la fin de la fonction existante
        if (adminData.structureId) {
          await fetchMedecinsTarifs(adminData.structureId);
          await fetchMedecinsRendezvous(adminData.structureId);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
        toast.error("Erreur lors du chargement des données");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  // Fonction pour récupérer les statistiques
  const fetchStats = async (structureId) => {
    try {
      // Statistiques de base
      const patientsQuery = query(collection(db, 'patients'), where("structureId", "==", structureId));
      const patientsSnapshot = await getDocs(patientsQuery);
      
      const medecinsQuery = query(collection(db, 'medecins'), where("structureId", "==", structureId));
      const medecinsSnapshot = await getDocs(medecinsQuery);

      setStats({
        patients: patientsSnapshot.size,
        medecins: medecinsSnapshot.size
      });

      // Données pour les graphiques
      
      // 1. Patients par mois (croissance mensuelle)
      const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
      const currentMonth = new Date().getMonth();
      
      // Récupérer tous les patients et grouper par date de création
      const patientsByMonth = [];
      let patientCount = 0;
      
      patientsSnapshot.forEach(doc => {
        const patientData = doc.data();
        if (patientData.createdAt) {
          const patientMonth = new Date(patientData.createdAt.toDate()).getMonth();
          // Accumuler les patients par mois pour montrer la croissance
          if (!patientsByMonth[patientMonth]) {
            patientsByMonth[patientMonth] = 0;
          }
          patientsByMonth[patientMonth]++;
        }
      });
      
      // Créer les données pour le graphique de patients
      const patientGraphData = [];
      for (let i = 0; i < 7; i++) {
        // Récupérer les 7 derniers mois
        const monthIndex = (currentMonth - 6 + i + 12) % 12;
        patientCount += patientsByMonth[monthIndex] || 0;
        patientGraphData.push({
          name: monthNames[monthIndex],
          count: patientCount
        });
      }
      
      setPatientData(patientGraphData.length > 0 ? patientGraphData : [
        { name: monthNames[currentMonth], count: patientsSnapshot.size }
      ]);
      
      // 2. Médecins par spécialité
      const specialtiesCounts = {};
      
      medecinsSnapshot.forEach(doc => {
        const medecinData = doc.data();
        if (medecinData.specialite) {
          if (!specialtiesCounts[medecinData.specialite]) {
            specialtiesCounts[medecinData.specialite] = 0;
          }
          specialtiesCounts[medecinData.specialite]++;
        }
      });
      
      const medecinGraphData = Object.keys(specialtiesCounts).map(specialty => ({
        name: specialty.length > 12 ? specialty.substring(0, 10) + '...' : specialty,
        count: specialtiesCounts[specialty]
      }));
      
      setMedecinData(medecinGraphData.length > 0 ? medecinGraphData : [
        { name: 'Médecins', count: medecinsSnapshot.size }
      ]);
    } catch (error) {
      console.error("Erreur lors de la récupération des statistiques:", error);
      toast.error("Erreur lors de la récupération des statistiques");
    }
  };

  // 2. Ajoutez un useEffect pour mettre à jour patientData quand stats.patients change
  useEffect(() => {
    if (stats.patients > 0 && patientData.length > 0) {
      // Mettre à jour le dernier point de données pour refléter le total actuel
      const updatedPatientData = [...patientData];
      updatedPatientData[updatedPatientData.length - 1].count = stats.patients;
      setPatientData(updatedPatientData);
    }
  }, [stats.patients]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSpecialiteChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      const currentSpecialites = Array.isArray(prev.specialites) ? prev.specialites : [];
      return {
        ...prev,
        specialites: checked 
          ? [...currentSpecialites, value]
          : currentSpecialites.filter(spec => spec !== value)
      };
    });
  };

  const handleAssuranceChange = (e) => {
    const { value, checked } = e.target;
    let newAssurances = [...formData.assurances];
    
    if (checked) {
      if (!newAssurances.includes(value)) {
        newAssurances.push(value);
      }
    } else {
      newAssurances = newAssurances.filter(assur => assur !== value);
    }
    
    setFormData({ ...formData, assurances: newAssurances });
  };

  const handleResetStructurePassword = async () => {
    try {
      setLoading(true);
      
      if (!formData.email) {
        toast.error("Email de la structure non disponible");
        return;
      }
      
      const auth = getAuth();
      await sendPasswordResetEmail(auth, formData.email);
      
      toast.success("Un email de réinitialisation de mot de passe a été envoyé à " + formData.email);
      
    } catch (error) {
      console.error("Erreur lors de l'envoi de l'email de réinitialisation:", error);
      toast.error("Erreur lors de l'envoi de l'email de réinitialisation");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChanges = async () => {
    try {
      setLoading(true);
      
      if (!admin?.structureId) {
        toast.error("ID de structure manquant");
        setLoading(false);
        return;
      }

      // Mise à jour des informations de la structure
      await updateDoc(doc(db, 'structures', admin.structureId), {
        nom: formData.nom,
        site: formData.site,
        telephone: formData.telephone,
        responsable: formData.responsable,
        specialites: formData.specialites,
        assurances: formData.assurances
      });

      // Si un nouveau mot de passe est défini, le mettre à jour
      if (formData.newPassword) {
        if (formData.newPassword !== formData.confirmPassword) {
          toast.error("Les mots de passe ne correspondent pas");
          setLoading(false);
          return;
        }

        if (!formData.currentPassword) {
          toast.error("Veuillez entrer votre mot de passe actuel");
          setLoading(false);
          return;
        }

        const auth = getAuth();
        const user = auth.currentUser;
        
        // Réauthentifier l'utilisateur avant de changer le mot de passe
          const credential = EmailAuthProvider.credential(user.email, formData.currentPassword);
          await reauthenticateWithCredential(user, credential);
          await updatePassword(user, formData.newPassword);
          
          // Réinitialiser les champs de mot de passe
          setFormData({
            ...formData,
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
          
          toast.success("Mot de passe mis à jour avec succès");
      }

      // Rafraîchir les données
      const structureDoc = await getDoc(doc(db, 'structures', admin.structureId));
      setStructure(structureDoc.data());
      
      toast.success("Modifications enregistrées avec succès");
      setEditMode(false);
    } catch (error) {
      console.error("Erreur lors de la sauvegarde des modifications:", error);
      toast.error("Erreur lors de la sauvegarde des modifications");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAdminChanges = async () => {
    try {
      setLoading(true);
      
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        toast.error("Vous devez être connecté pour effectuer cette action");
        return;
      }
      
      // Mise à jour des informations de l'administrateur
      await updateDoc(doc(db, 'admins', user.uid), {
        nom: formData.adminNom || admin?.nom,
        prenom: formData.adminPrenom || admin?.prenom,
        telephone: formData.adminTelephone || admin?.telephone,
        adminEmailNotifications: formData.adminEmailNotifications || admin?.adminEmailNotifications,
        adminNewPatientAlert: formData.adminNewPatientAlert || admin?.adminNewPatientAlert,
        adminLoginAlert: formData.adminLoginAlert || admin?.adminLoginAlert
      });
      
      // Mise à jour du mot de passe si nécessaire
      if (formData.adminNewPassword) {
        if (formData.adminNewPassword !== formData.adminConfirmPassword) {
          toast.error("Les mots de passe ne correspondent pas");
          setLoading(false);
          return;
        }
        
        if (!formData.adminCurrentPassword) {
          toast.error("Veuillez entrer votre mot de passe actuel");
          setLoading(false);
          return;
        }
        
        try {
          // Réauthentifier l'utilisateur avant de changer le mot de passe
          const credential = EmailAuthProvider.credential(user.email, formData.adminCurrentPassword);
          await reauthenticateWithCredential(user, credential);
          await updatePassword(user, formData.adminNewPassword);
          
          // Réinitialiser les champs de mot de passe
          setFormData({
            ...formData,
            adminCurrentPassword: '',
            adminNewPassword: '',
            adminConfirmPassword: ''
          });
          
          toast.success("Mot de passe administrateur mis à jour avec succès");
        } catch (error) {
          console.error("Erreur lors de la mise à jour du mot de passe:", error);
          toast.error("Mot de passe actuel incorrect");
          setLoading(false);
          return;
        }
      }

      // Mettre à jour les données locales
      const adminDoc = await getDoc(doc(db, 'admins', user.uid));
      setAdmin(adminDoc.data());
      
      toast.success("Profil administrateur mis à jour avec succès");
      setEditMode(false);
    } catch (error) {
      console.error("Erreur lors de la sauvegarde des modifications:", error);
      toast.error("Erreur lors de la sauvegarde des modifications");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      toast.success("Déconnexion réussie");
      navigate('/');
    } catch (error) {
      console.error("Erreur lors de la déconnexion:", error);
      toast.error("Erreur lors de la déconnexion");
    }
  };

  // 3. Ajouter cette fonction pour charger les médecins avec leurs tarifs
  const fetchMedecinsTarifs = async (structureId) => {
    try {
      const medecinsQuery = query(collection(db, 'medecins'), where("structureId", "==", structureId));
      const medecinsSnapshot = await getDocs(medecinsQuery);
      
      const medecinsData = [];
      medecinsSnapshot.forEach(doc => {
        const medecinData = doc.data();
        medecinsData.push({
          id: doc.id,
          nom: medecinData.nom || '',
          prenom: medecinData.prenom || '',
          specialite: medecinData.specialite || '',
          tarif: medecinData.tarif || 0,
        });
      });
      
      setMedecinsFinance(medecinsData);
      await calculerRevenus(medecinsData, periodeRevenu);
    } catch (error) {
      console.error("Erreur lors de la récupération des médecins:", error);
      toast.error("Erreur lors de la récupération des médecins");
    }
  };

  // 4. Ajouter cette fonction pour calculer les revenus
  const calculerRevenus = async (medecins, periode) => {
    try {
      if (!admin?.structureId || !medecins.length) return;
      
      // Préparer les dates pour la période
      const maintenant = new Date();
      let debut, labels, format;
      
      switch(periode) {
        case 'jours':
          debut = new Date(maintenant);
          debut.setDate(maintenant.getDate() - 7);
          labels = Array.from({length: 7}, (_, i) => {
            const date = new Date(debut);
            date.setDate(debut.getDate() + i);
            return date.toLocaleDateString('fr-FR', {weekday: 'short'});
          });
          format = (date) => date.toLocaleDateString('fr-FR', {weekday: 'short'});
          break;
        case 'semaines':
          debut = new Date(maintenant);
          debut.setDate(maintenant.getDate() - 28);
          labels = Array.from({length: 4}, (_, i) => `S${i+1}`);
          format = (date) => `S${Math.floor((date - debut) / (7 * 24 * 60 * 60 * 1000)) + 1}`;
          break;
        case 'mois':
          debut = new Date(maintenant);
          debut.setMonth(maintenant.getMonth() - 6);
          labels = Array.from({length: 6}, (_, i) => {
            const date = new Date(debut);
            date.setMonth(debut.getMonth() + i);
            return date.toLocaleDateString('fr-FR', {month: 'short'});
          });
          format = (date) => date.toLocaleDateString('fr-FR', {month: 'short'});
          break;
        case 'annee':
          debut = new Date(maintenant);
          debut.setFullYear(maintenant.getFullYear() - 1);
          labels = Array.from({length: 12}, (_, i) => {
            const date = new Date(debut);
            date.setMonth(debut.getMonth() + i);
            return date.toLocaleDateString('fr-FR', {month: 'short'});
          });
          format = (date) => date.toLocaleDateString('fr-FR', {month: 'short'});
          break;
        default:
          debut = new Date(maintenant);
          debut.setMonth(maintenant.getMonth() - 6);
          labels = Array.from({length: 6}, (_, i) => {
            const date = new Date(debut);
            date.setMonth(debut.getMonth() + i);
            return date.toLocaleDateString('fr-FR', {month: 'short'});
          });
          format = (date) => date.toLocaleDateString('fr-FR', {month: 'short'});
      }
      
      // Créer un objet pour stocker les revenus par période
      const revenus = {};
      labels.forEach(label => {
        revenus[label] = 0;
      });
      
      // Créer un objet pour compter les rendez-vous par médecin et par période
      const rdvsParMedecinEtPeriode = {};
      medecins.forEach(medecin => {
        rdvsParMedecinEtPeriode[medecin.id] = {};
        labels.forEach(label => {
          rdvsParMedecinEtPeriode[medecin.id][label] = 0;
        });
      });
      
      // Récupérer tous les rendez-vous
      const rdvsQuery = query(
        collection(db, 'rendezvous'), 
        where("structureId", "==", admin.structureId),
        where("date", ">=", debut)
      );
      const rdvsSnapshot = await getDocs(rdvsQuery);
      
      // Compter les rendez-vous par médecin et par période
      rdvsSnapshot.forEach(doc => {
        const rdv = doc.data();
        if (rdv.date && rdv.medecinId) {
          const rdvDate = rdv.date.toDate();
          const periodLabel = format(rdvDate);
          
          if (labels.includes(periodLabel) && rdvsParMedecinEtPeriode[rdv.medecinId]) {
            rdvsParMedecinEtPeriode[rdv.medecinId][periodLabel]++;
          }
        }
      });
      
      // Calculer les revenus (tarif × nombre de rendez-vous)
      let total = 0;
      
      medecins.forEach(medecin => {
        if (medecin.tarif) {
          labels.forEach(label => {
            const nombreRdvs = rdvsParMedecinEtPeriode[medecin.id][label] || 0;
            const revenuPeriode = nombreRdvs * parseFloat(medecin.tarif);
            
            revenus[label] += revenuPeriode;
            total += revenuPeriode;
          });
        }
      });
      
      // Formater les données pour le graphique
      const graphData = labels.map(label => ({
        name: label,
        montant: revenus[label]
      }));
      
      setRevenusData(graphData);
      setRevenuTotal(total);
      
    } catch (error) {
      console.error("Erreur lors du calcul des revenus:", error);
      toast.error("Erreur lors du calcul des revenus");
    }
  };

  // 6. Ajouter une fonction pour mettre à jour le tarif d'un médecin
  const handleTarifChange = async (medecinId, tarif) => {
    try {
      setLoading(true);
      
      // Mettre à jour dans Firebase
      await updateDoc(doc(db, 'medecins', medecinId), {
        tarif: parseFloat(tarif)
      });
      
      // Mettre à jour l'état local
      const updatedMedecins = medecinsFinance.map(medecin => 
        medecin.id === medecinId ? {...medecin, tarif: parseFloat(tarif)} : medecin
      );
      
      setMedecinsFinance(updatedMedecins);
      await calculerRevenus(updatedMedecins, periodeRevenu);
      
      toast.success("Tarif mis à jour avec succès");
    
    } catch (error) {
      console.error("Erreur lors de la mise à jour du tarif:", error);
      toast.error("Erreur lors de la mise à jour du tarif");
    } finally {
      setLoading(false);
    }
  };

  // 7. Ajouter une fonction pour changer la période
  const handlePeriodeChange = (periode) => {
    setPeriodeRevenu(periode);
    calculerRevenus(medecinsFinance, periode);
  };

  // Ajouter cette fonction pour générer le rapport d'un médecin spécifique
  const genererRapportMedecin = async (medecinId, periode) => {
    try {
      setLoading(true);
      
      if (!medecinId || !admin?.structureId) {
        return;
      }
      
      const medecin = medecinsFinance.find(m => m.id === medecinId);
      if (!medecin) return;
      
      // Préparer les dates pour la période
      const maintenant = new Date();
      let debut, labels, format;
      
      switch(periode) {
        case 'jours':
          debut = new Date(maintenant);
          debut.setDate(maintenant.getDate() - 7);
          labels = Array.from({length: 7}, (_, i) => {
            const date = new Date(debut);
            date.setDate(debut.getDate() + i);
            return date.toLocaleDateString('fr-FR', {weekday: 'short'});
          });
          format = (date) => date.toLocaleDateString('fr-FR', {weekday: 'short'});
          break;
        case 'semaines':
          debut = new Date(maintenant);
          debut.setDate(maintenant.getDate() - 28);
          labels = Array.from({length: 4}, (_, i) => `S${i+1}`);
          format = (date) => {
            const diff = Math.floor((date - debut) / (7 * 24 * 60 * 60 * 1000));
            return `S${diff + 1}`;
          };
          break;
        case 'annee':
          debut = new Date(maintenant);
          debut.setFullYear(maintenant.getFullYear() - 1);
          labels = Array.from({length: 12}, (_, i) => {
            const date = new Date(debut);
            date.setMonth(debut.getMonth() + i);
            return date.toLocaleDateString('fr-FR', {month: 'short'});
          });
          format = (date) => date.toLocaleDateString('fr-FR', {month: 'short'});
          break;
        case 'mois':
        default:
          debut = new Date(maintenant);
          debut.setMonth(maintenant.getMonth() - 6);
          labels = Array.from({length: 6}, (_, i) => {
            const date = new Date(debut);
            date.setMonth(debut.getMonth() + i);
            return date.toLocaleDateString('fr-FR', {month: 'short'});
          });
          format = (date) => date.toLocaleDateString('fr-FR', {month: 'short'});
      }
      
      // Récupérer tous les RDVs de ce médecin pour la période
      const rdvsQuery = query(
        collection(db, 'rendezvous'),
        where("structureId", "==", admin.structureId),
        where("medecinId", "==", medecinId),
        where("date", ">=", debut)
      );
      
      const rdvsSnapshot = await getDocs(rdvsQuery);
      
      // Initialiser les compteurs par période
      const rdvsByPeriod = {};
      labels.forEach(label => { rdvsByPeriod[label] = 0 });
      
      let totalRevenu = 0;
      
      rdvsSnapshot.forEach(doc => {
        const rdv = doc.data();
        if (rdv.date) {
          const rdvDate = rdv.date.toDate();
          const periodLabel = format(rdvDate);
          
          // Compter les RDVs par période
          if (labels.includes(periodLabel)) {
            rdvsByPeriod[periodLabel] = (rdvsByPeriod[periodLabel] || 0) + 1;
            
            // Calculer le revenu (basé sur le tarif du médecin)
            totalRevenu += parseFloat(medecin.tarif || 0);
          }
        }
      });
      
      // Formater les données pour le graphique
      const periodeData = labels.map(label => ({
        name: label,
        rdvs: rdvsByPeriod[label] || 0,
        revenu: (rdvsByPeriod[label] || 0) * parseFloat(medecin.tarif || 0)
      }));
      
      // Mettre à jour le rapport
      setMedecinRapport({
        rdvCount: rdvsSnapshot.size,
        revenuTotal: totalRevenu,
        periodeData: periodeData
      });
      
    } catch (error) {
      console.error("Erreur lors de la génération du rapport:", error);
      toast.error("Erreur lors de la génération du rapport");
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour gérer le changement de période du rapport médecin
  const handlePeriodeMedecinChange = (periode) => {
    setPeriodeMedecinRapport(periode);
    if (selectedMedecinId) {
      genererRapportMedecin(selectedMedecinId, periode);
    }
  };

  // Mise à jour du useEffect pour utiliser la période sélectionnée
  useEffect(() => {
    if (selectedMedecinId) {
      genererRapportMedecin(selectedMedecinId, periodeMedecinRapport);
    }
  }, [selectedMedecinId]);

  // Fonction pour exporter en Excel
  const exportToExcel = () => {
    if (!selectedMedecinId || !medecinRapport.periodeData.length) {
      toast.error("Aucune donnée à exporter");
      return;
    }
    
    const medecin = medecinsFinance.find(m => m.id === selectedMedecinId);
    const wb = XLSX.utils.book_new();
    
    // Créer la feuille de résumé
    const summaryData = [
      ["Rapport financier médecin"],
      [`Dr. ${medecin.prenom} ${medecin.nom} - ${medecin.specialite || "Non spécifié"}`],
      [""],
      ["Période", periodeMedecinRapport === 'jours' ? "7 derniers jours" : 
             periodeMedecinRapport === 'semaines' ? "4 dernières semaines" : 
             periodeMedecinRapport === 'annee' ? "12 derniers mois" : "6 derniers mois"],
      ["Total consultations", medecinRapport.rdvCount],
      ["Revenu total", `${medecinRapport.revenuTotal.toLocaleString()} FCFA`],
      ["Revenu moyen par consultation", `${(medecinRapport.rdvCount > 0 ? Math.round(medecinRapport.revenuTotal / medecinRapport.rdvCount) : 0).toLocaleString()} FCFA`],
      [""],
      ["Détails par période"]
    ];
    
    // Ajouter les en-têtes
    summaryData.push(["Période", "Consultations", "Revenus (FCFA)"]);
    
    // Ajouter les données par période
    medecinRapport.periodeData.forEach(item => {
      summaryData.push([item.name, item.rdvs, item.revenu]);
    });
    
    const ws = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, ws, "Rapport");
    
    // Générer le fichier
    const fileName = `Rapport_${medecin.prenom}_${medecin.nom}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
    
    toast.success("Rapport exporté avec succès en Excel");
  };

  // Fonction pour exporter en PDF
  const exportToPDF = async () => {
    if (!selectedMedecinId || !medecinRapport.periodeData.length) {
      toast.error("Aucune donnée à exporter");
      return;
    }
    
    const medecin = medecinsFinance.find(m => m.id === selectedMedecinId);
    const doc = new jsPDF();
    
    // Titre et sous-titre
    doc.setFontSize(18);
    doc.text("Rapport financier médecin", 14, 20);
    
    doc.setFontSize(14);
    doc.text(`Dr. ${medecin.prenom} ${medecin.nom} - ${medecin.specialite || "Non spécifié"}`, 14, 30);
    
    // Période
    doc.setFontSize(12);
    doc.text(`Période: ${
      periodeMedecinRapport === 'jours' ? "7 derniers jours" : 
      periodeMedecinRapport === 'semaines' ? "4 dernières semaines" : 
      periodeMedecinRapport === 'annee' ? "12 derniers mois" : "6 derniers mois"
    }`, 14, 40);
    
    // Résumé
    doc.text("Résumé:", 14, 50);
    doc.text(`Total consultations: ${medecinRapport.rdvCount}`, 14, 60);
    doc.text(`Revenu total: ${medecinRapport.revenuTotal.toLocaleString()} FCFA`, 14, 70);
    doc.text(`Revenu moyen: ${(medecinRapport.rdvCount > 0 ? Math.round(medecinRapport.revenuTotal / medecinRapport.rdvCount) : 0).toLocaleString()} FCFA`, 14, 80);
    
    // Tableau de données
    const tableColumn = ["Période", "Consultations", "Revenus (FCFA)"];
    const tableRows = medecinRapport.periodeData.map(item => [
      item.name,
      item.rdvs,
      item.revenu.toLocaleString()
    ]);
    
    autoTable(doc, {
      startY: 90,
      head: [tableColumn],
      body: tableRows,
      theme: 'grid',
      styles: { fontSize: 10 }
    });
    
    // Graphique
    try {
      const chartElement = document.getElementById('rapport-chart');
      if (chartElement) {
        const canvas = await html2canvas(chartElement);
        const imgData = canvas.toDataURL('image/png');
        doc.addPage();
        doc.text("Graphique de performance", 14, 20);
        doc.addImage(imgData, 'PNG', 10, 30, 190, 100);
      }
    } catch (error) {
      console.error("Erreur lors de la capture du graphique:", error);
    }
    
    // Générer le fichier
    const fileName = `Rapport_${medecin.prenom}_${medecin.nom}_${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(fileName);
    
    toast.success("Rapport exporté avec succès en PDF");
  };

  // Fonction pour exporter en PowerPoint
  const exportToPPT = async () => {
    if (!selectedMedecinId || !medecinRapport.periodeData.length) {
      toast.error("Aucune donnée à exporter");
      return;
    }
    
    const medecin = medecinsFinance.find(m => m.id === selectedMedecinId);
    const pptx = new pptxgen();
    
    // Slide 1: Titre
    let slide = pptx.addSlide();
    slide.addText("Rapport financier médecin", { x: 1, y: 1, fontSize: 24, bold: true });
    slide.addText(`Dr. ${medecin.prenom} ${medecin.nom} - ${medecin.specialite || "Non spécifié"}`, { x: 1, y: 1.5, fontSize: 18 });
    slide.addText(`Structure: ${structure?.nom || ''}`, { x: 1, y: 2, fontSize: 14 });
    slide.addText(`Période: ${
      periodeMedecinRapport === 'jours' ? "7 derniers jours" : 
      periodeMedecinRapport === 'semaines' ? "4 dernières semaines" : 
      periodeMedecinRapport === 'annee' ? "12 derniers mois" : "6 derniers mois"
    }`, { x: 1, y: 2.5, fontSize: 14 });
    
    // Slide 2: Résumé
    slide = pptx.addSlide();
    slide.addText("Résumé des performances", { x: 1, y: 0.5, fontSize: 18, bold: true });
    
    // Ajouter des informations clés
    slide.addText(`Total consultations: ${medecinRapport.rdvCount}`, { x: 1, y: 1.2, fontSize: 16 });
    slide.addText(`Revenu total: ${medecinRapport.revenuTotal.toLocaleString()} FCFA`, { x: 1, y: 1.7, fontSize: 16 });
    slide.addText(`Revenu moyen par consultation: ${(medecinRapport.rdvCount > 0 ? Math.round(medecinRapport.revenuTotal / medecinRapport.rdvCount) : 0).toLocaleString()} FCFA`, { x: 1, y: 2.2, fontSize: 16 });
    
    // Slide 3: Détails
    slide = pptx.addSlide();
    slide.addText("Détails par période", { x: 1, y: 0.5, fontSize: 18, bold: true });
    
    // Créer un tableau
    const tableData = [
      ["Période", "Consultations", "Revenus (FCFA)"],
      ...medecinRapport.periodeData.map(item => [item.name, item.rdvs.toString(), item.revenu.toLocaleString()])
    ];
    
    slide.addTable(tableData, { x: 1, y: 1.2, w: 8, fontSize: 14 });
    
    // Slide 4: Graphique
    try {
      const chartElement = document.getElementById('rapport-chart');
      if (chartElement) {
        const canvas = await html2canvas(chartElement);
        const imgData = canvas.toDataURL('image/png');
        
        slide = pptx.addSlide();
        slide.addText("Visualisation des performances", { x: 1, y: 0.5, fontSize: 18, bold: true });
        slide.addImage({ data: imgData, x: 0.5, y: 1.2, w: 9, h: 4.5 });
      }
    } catch (error) {
      console.error("Erreur lors de la capture du graphique:", error);
    }
    
    // Générer le fichier
    const fileName = `Rapport_${medecin.prenom}_${medecin.nom}_${new Date().toISOString().slice(0, 10)}.pptx`;
    pptx.writeFile({ fileName });
    
    toast.success("Rapport exporté avec succès en PowerPoint");
  };

  // Version modifiée pour exportToWord
  const exportToWord = async () => {
    if (!selectedMedecinId || !medecinRapport.periodeData.length) {
      toast.error("Aucune donnée à exporter");
      return;
    }
    
    const medecin = medecinsFinance.find(m => m.id === selectedMedecinId);
    
    // Créer un document HTML temporaire
    const tempDiv = document.createElement('div');
    tempDiv.style.display = 'none';
    document.body.appendChild(tempDiv);
    
    // Styler le document Word
    tempDiv.innerHTML = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word'>
      <head>
        <meta charset="utf-8">
        <title>Rapport Médecin</title>
        <style>
          table { border-collapse: collapse; width: 100%; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; }
          h1, h2, h3 { font-family: Arial, sans-serif; }
          .header { text-align: center; margin-bottom: 20px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Rapport financier médecin</h1>
          <h2>Dr. ${medecin.prenom} ${medecin.nom} - ${medecin.specialite || "Non spécifié"}</h2>
          <p>Période: ${
            periodeMedecinRapport === 'jours' ? "7 derniers jours" : 
            periodeMedecinRapport === 'semaines' ? "4 dernières semaines" : 
            periodeMedecinRapport === 'annee' ? "12 derniers mois" : "6 derniers mois"
          }</p>
        </div>
        
        <h3>Résumé</h3>
        <p>Total consultations: ${medecinRapport.rdvCount}</p>
        <p>Revenu total: ${medecinRapport.revenuTotal.toLocaleString()} FCFA</p>
        <p>Revenu moyen par consultation: ${(medecinRapport.rdvCount > 0 ? Math.round(medecinRapport.revenuTotal / medecinRapport.rdvCount) : 0).toLocaleString()} FCFA</p>
        
        <h3>Détails par période</h3>
        <table>
          <tr>
            <th>Période</th>
            <th>Consultations</th>
            <th>Revenus (FCFA)</th>
          </tr>
          ${medecinRapport.periodeData.map(item => `
            <tr>
              <td>${item.name}</td>
              <td>${item.rdvs}</td>
              <td>${item.revenu.toLocaleString()}</td>
            </tr>
          `).join('')}
        </table>
      </body>
      </html>
    `;
    
    // Créer le Blob et télécharger
    const fileName = `Rapport_${medecin.prenom}_${medecin.nom}_${new Date().toISOString().slice(0, 10)}.doc`;
    const blob = new Blob([tempDiv.innerHTML], {type: 'application/msword;charset=utf-8'});
    saveAs(blob, fileName);
    
    // Nettoyer
    document.body.removeChild(tempDiv);
    
    toast.success("Rapport exporté avec succès en Word");
  };

  // Fonction pour exporter les revenus généraux en Excel
  const exportRevenusToExcel = () => {
    if (!revenusData.length) {
      toast.error("Aucune donnée à exporter");
      return;
    }
    
    const wb = XLSX.utils.book_new();
    
    // Créer la feuille de résumé
    const summaryData = [
      ["Rapport de revenus de la structure"],
      [structure?.nom || ''],
      [""],
      ["Période", periodeRevenu === 'jours' ? "7 derniers jours" : 
               periodeRevenu === 'semaines' ? "4 dernières semaines" : 
               periodeRevenu === 'annee' ? "12 derniers mois" : "6 derniers mois"],
      ["Revenu total", `${revenuTotal.toLocaleString()} FCFA`],
      [""],
      ["Détails par période"]
    ];
    
    // Ajouter les en-têtes
    summaryData.push(["Période", "Revenus (FCFA)"]);
    
    // Ajouter les données par période
    revenusData.forEach(item => {
      summaryData.push([item.name, item.montant]);
    });
    
    const ws = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, ws, "Revenus");
    
    // Générer le fichier
    const fileName = `Revenus_${structure?.nom || 'Structure'}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
    
    toast.success("Rapport de revenus exporté avec succès en Excel");
  };

  // Version modifiée pour exportRevenusToWord
  const exportRevenusToWord = async () => {
    if (!revenusData.length) {
      toast.error("Aucune donnée à exporter");
      return;
    }
    
    // Créer un document HTML temporaire
    const tempDiv = document.createElement('div');
    tempDiv.style.display = 'none';
    document.body.appendChild(tempDiv);
    
    // Styler le document Word
    tempDiv.innerHTML = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word'>
      <head>
        <meta charset="utf-8">
        <title>Rapport Revenus</title>
        <style>
          table { border-collapse: collapse; width: 100%; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; }
          h1, h2, h3 { font-family: Arial, sans-serif; }
          .header { text-align: center; margin-bottom: 20px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Rapport de revenus de la structure</h1>
          <h2>${structure?.nom || ''}</h2>
          <p>Période: ${
            periodeRevenu === 'jours' ? "7 derniers jours" : 
            periodeRevenu === 'semaines' ? "4 dernières semaines" : 
            periodeRevenu === 'annee' ? "12 derniers mois" : "6 derniers mois"
          }</p>
        </div>
        
        <h3>Résumé</h3>
        <p>Revenu total: ${revenuTotal.toLocaleString()} FCFA</p>
        
        <h3>Détails par période</h3>
        <table>
          <tr>
            <th>Période</th>
            <th>Revenus (FCFA)</th>
          </tr>
          ${revenusData.map(item => `
            <tr>
              <td>${item.name}</td>
              <td>${item.montant.toLocaleString()}</td>
            </tr>
          `).join('')}
        </table>
      </body>
      </html>
    `;
    
    // Créer le Blob et télécharger
    const fileName = `Revenus_${structure?.nom || 'Structure'}_${new Date().toISOString().slice(0, 10)}.doc`;
    const blob = new Blob([tempDiv.innerHTML], {type: 'application/msword;charset=utf-8'});
    saveAs(blob, fileName);
    
    // Nettoyer
    document.body.removeChild(tempDiv);
    
    toast.success("Rapport de revenus exporté avec succès en Word");
  };

  // Fonction pour exporter les revenus généraux en PDF
  const exportRevenusToPDF = async () => {
    if (!revenusData.length) {
      toast.error("Aucune donnée à exporter");
      return;
    }
    
    const doc = new jsPDF();
    
    // Titre et sous-titre
    doc.setFontSize(18);
    doc.text("Rapport de revenus de la structure", 14, 20);
    
    doc.setFontSize(14);
    doc.text(structure?.nom || '', 14, 30);
    
    // Période
    doc.setFontSize(12);
    doc.text(`Période: ${
      periodeRevenu === 'jours' ? "7 derniers jours" : 
      periodeRevenu === 'semaines' ? "4 dernières semaines" : 
      periodeRevenu === 'annee' ? "12 derniers mois" : "6 derniers mois"
    }`, 14, 40);
    
    // Résumé
    doc.text("Résumé:", 14, 50);
    doc.text(`Revenu total: ${revenuTotal.toLocaleString()} FCFA`, 14, 60);
    
    // Tableau de données
    const tableColumn = ["Période", "Revenus (FCFA)"];
    const tableRows = revenusData.map(item => [
      item.name,
      item.montant.toLocaleString()
    ]);
    
    autoTable(doc, {
      startY: 70,
      head: [tableColumn],
      body: tableRows,
      theme: 'grid',
      styles: { fontSize: 10 }
    });
    
    // Graphique
    try {
      const chartElement = document.getElementById('revenus-chart');
      if (chartElement) {
        const canvas = await html2canvas(chartElement);
        const imgData = canvas.toDataURL('image/png');
        doc.addPage();
        doc.text("Graphique des revenus", 14, 20);
        doc.addImage(imgData, 'PNG', 10, 30, 190, 100);
      }
    } catch (error) {
      console.error("Erreur lors de la capture du graphique:", error);
    }
    
    // Générer le fichier
    const fileName = `Revenus_${structure?.nom || 'Structure'}_${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(fileName);
    
    toast.success("Rapport de revenus exporté avec succès en PDF");
  };

  // Fonction pour exporter les revenus généraux en PowerPoint
  const exportRevenusToPPT = async () => {
    if (!revenusData.length) {
      toast.error("Aucune donnée à exporter");
      return;
    }
    
    const pptx = new pptxgen();
    
    // Slide 1: Titre
    let slide = pptx.addSlide();
    slide.addText("Rapport de revenus de la structure", { x: 1, y: 1, fontSize: 24, bold: true });
    slide.addText(structure?.nom || '', { x: 1, y: 1.5, fontSize: 18 });
    slide.addText(`Période: ${
      periodeRevenu === 'jours' ? "7 derniers jours" : 
      periodeRevenu === 'semaines' ? "4 dernières semaines" : 
      periodeRevenu === 'annee' ? "12 derniers mois" : "6 derniers mois"
    }`, { x: 1, y: 2, fontSize: 14 });
    
    // Slide 2: Résumé
    slide = pptx.addSlide();
    slide.addText("Résumé des revenus", { x: 1, y: 0.5, fontSize: 18, bold: true });
    
    // Ajouter des informations clés
    slide.addText(`Revenu total: ${revenuTotal.toLocaleString()} FCFA`, { x: 1, y: 1.5, fontSize: 16 });
    
    // Slide 3: Détails
    slide = pptx.addSlide();
    slide.addText("Détails par période", { x: 1, y: 0.5, fontSize: 18, bold: true });
    
    // Créer un tableau
    const tableData = [
      ["Période", "Revenus (FCFA)"],
      ...revenusData.map(item => [item.name, item.montant.toLocaleString()])
    ];
    
    slide.addTable(tableData, { x: 1, y: 1.2, w: 8, fontSize: 14 });
    
    // Slide 4: Graphique
    try {
      const chartElement = document.getElementById('revenus-chart');
      if (chartElement) {
        const canvas = await html2canvas(chartElement);
        const imgData = canvas.toDataURL('image/png');
        
        slide = pptx.addSlide();
        slide.addText("Visualisation des revenus", { x: 1, y: 0.5, fontSize: 18, bold: true });
        slide.addImage({ data: imgData, x: 0.5, y: 1.2, w: 9, h: 4.5 });
      }
    } catch (error) {
      console.error("Erreur lors de la capture du graphique:", error);
    }
    
    // Générer le fichier
    const fileName = `Revenus_${structure?.nom || 'Structure'}_${new Date().toISOString().slice(0, 10)}.pptx`;
    pptx.writeFile({ fileName });
    
    toast.success("Rapport de revenus exporté avec succès en PowerPoint");
  };

  // Fonction pour récupérer les détails des rendez-vous d'un médecin spécifique
  const getDetailsRendezvousMedecin = (medecinId) => {
    if (!medecinId) {
      setDetailsRendezvousMedecin([]);
      return;
    }
    
    // Trouver le médecin dans la liste
    const medecin = medecinRendezvous.find(m => m.id === medecinId);
    
    if (!medecin || !medecin.rdvs || medecin.rdvs.length === 0) {
      setDetailsRendezvousMedecin([]);
      return;
    }
    
    // Grouper les rendez-vous par date
    const rdvsByDate = {};
    
    medecin.rdvs.forEach(rdv => {
      if (rdv.date) {
        const rdvDate = rdv.date.toDate ? rdv.date.toDate() : new Date(rdv.date);
        const dateStr = rdvDate.toISOString().split('T')[0]; // format YYYY-MM-DD
        
        if (!rdvsByDate[dateStr]) {
          rdvsByDate[dateStr] = {
            date: rdvDate,
            dateStr: dateStr,
            count: 0,
            patients: []
          };
        }
        
        rdvsByDate[dateStr].count += 1;
        
        // Ajouter le patient si disponible
        if (rdv.patientNom) {
          rdvsByDate[dateStr].patients.push({
            id: rdv.patientId || 'inconnu',
            nom: rdv.patientNom || 'Patient',
            prenom: rdv.patientPrenom || '',
            heure: rdv.heure || ''
          });
        }
      }
    });
    
    // Convertir en tableau et trier par date (plus récente d'abord)
    const detailsList = Object.values(rdvsByDate).sort((a, b) => b.date - a.date);
    
    setDetailsRendezvousMedecin(detailsList);
    setSelectedMedecinForDetail(medecin);
  };

  // Fonction pour calculer le revenu pour une date ou période spécifique
  const calculerRevenuPourDate = (dateStr, tarif) => {
    const rdvsDate = detailsRendezvousMedecin.find(d => d.dateStr === dateStr);
    if (rdvsDate) {
      return rdvsDate.count * tarif;
    }
    return 0;
  };

  // Fonction pour formater la date en format lisible
  const formatDate = (dateStr) => {
    // Convertir YYYY-MM-DD en format localisé
    const [year, month, day] = dateStr.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Fonction pour récupérer le nombre total de rendez-vous par période
  const getRendezvousTotalParPeriode = (medecins) => {
    // Total pour tous les médecins
    let totalRdv = 0;
    let revenusTotal = 0;
    
    // Compter par médecin pour les statistiques comparatives
    const statsMedecins = [];
    
    medecins.forEach(medecin => {
      totalRdv += medecin.nbRendezvous;
      
      // Calculer le revenu total potentiel pour ce médecin
      const revenuMedecin = medecin.nbRendezvous * medecin.tarif;
      revenusTotal += revenuMedecin;
      
      // Ajouter aux statistiques
      statsMedecins.push({
        id: medecin.id,
        nom: `Dr. ${medecin.prenom} ${medecin.nom}`,
        specialite: medecin.specialite,
        nbRendezvous: medecin.nbRendezvous,
        revenu: revenuMedecin,
        tarif: medecin.tarif,
        pourcentageRdv: 0, // Sera calculé ci-dessous
        pourcentageRevenu: 0 // Sera calculé ci-dessous
      });
    });
    
    // Calculer les pourcentages
    statsMedecins.forEach(stat => {
      stat.pourcentageRdv = totalRdv > 0 ? (stat.nbRendezvous / totalRdv) * 100 : 0;
      stat.pourcentageRevenu = revenusTotal > 0 ? (stat.revenu / revenusTotal) * 100 : 0;
    });
    
    // Trier par nombre de rendez-vous décroissant
    statsMedecins.sort((a, b) => b.nbRendezvous - a.nbRendezvous);
    
    return {
      totalRdv,
      revenusTotal,
      statsMedecins
    };
  };

  // Modifier la fonction renderMedecinsRendezvous pour le nouveau format
  const renderMedecinsRendezvous = () => {
    // Calculer les statistiques
    const stats = getRendezvousTotalParPeriode(medecinRendezvous);

    return (
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <FaUserMd className="me-2" />
            Médecins et leurs rendez-vous
          </h5>
          <button
            className="btn btn-sm btn-danger"
            onClick={reinitialiserCalculsTousMedecins}
            title="Réinitialiser tous les calculs pour tous les médecins"
          >
            <i className="fas fa-trash-alt me-1"></i> Réinitialiser tous les calculs
          </button>
        </div>

        {/* Barre de recherche */}
        <div className="card-body border-bottom pb-3">
          <div className="ms-search-box">
            <input
              type="text"
              className="ms-search-input"
              placeholder="Rechercher un médecin..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button 
                className="ms-search-clear"
                onClick={() => setSearchTerm('')}
                title="Effacer la recherche"
              >
                ×
              </button>
            )}
          </div>
        </div>

        <div className="card-body">
          {medecinRendezvous.length === 0 ? (
            <p className="text-center">Aucun médecin trouvé</p>
          ) : (
            <>
              {/* Tableau des médecins avec tarifs */}
              <div className="table-responsive">
                <table className="table table-bordered table-hover">
                  <thead className="table-primary">
                    <tr>
                      <th>Médecin</th>
                      <th>Spécialité</th>
                      <th>Total RDV</th>
                      <th>Tarif (FCFA)</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {medecinRendezvous
                      .filter(medecin => {
                        if (!searchTerm) return true;
                        const searchTermLower = searchTerm.toLowerCase();
                        return (
                          medecin.nom.toLowerCase().includes(searchTermLower) ||
                          medecin.prenom.toLowerCase().includes(searchTermLower) ||
                          medecin.specialite.toLowerCase().includes(searchTermLower)
                        );
                      })
                      .map(medecin => (
                      <tr key={medecin.id} className={selectedMedecinForDetail?.id === medecin.id ? 'table-info' : ''}>
                        <td>Dr. {medecin.prenom} {medecin.nom}</td>
                        <td>{medecin.specialite}</td>
                        <td className="text-center">
                          <span className="badge bg-info rounded-pill">
                            {medecin.nbRendezvous}
                          </span>
                        </td>
                        <td>
                          <div className="input-group input-group-sm">
                            <input
                              type="number"
                              className="form-control text-end"
                              value={medecin.tarif}
                              onChange={(e) => {
                                const newMedecins = [...medecinRendezvous];
                                const index = newMedecins.findIndex(m => m.id === medecin.id);
                                newMedecins[index].tarif = e.target.value;
                                setMedecinRendezvous(newMedecins);
                              }}
                              min="0"
                            />
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => handleTarifMedecinRendezvous(medecin.id, medecin.tarif)}
                              title="Enregistrer le tarif"
                            >
                              <i className="fas fa-save"></i>
                            </button>
                          </div>
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <button 
                              className="btn btn-outline-primary"
                              onClick={() => getDetailsRendezvousMedecin(medecin.id)}
                              title="Voir les détails des rendez-vous"
                            >
                              <i className="fas fa-eye me-1"></i> Détails
                            </button>
                            <button 
                              className="btn btn-outline-danger"
                              onClick={() => reinitialiserCalculsMedecin(medecin.id)}
                              title="Réinitialiser les calculs pour ce médecin"
                            >
                              <i className="fas fa-redo-alt"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-secondary">
                    <tr>
                      <td colSpan="2" className="text-end fw-bold">Total:</td>
                      <td className="text-center">
                        <span className="badge bg-primary rounded-pill">
                          {stats.totalRdv}
                        </span>
                      </td>
                      <td colSpan="2" className="text-end fw-bold">
                        Revenus potentiels: {stats.revenusTotal.toLocaleString()} FCFA
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              
              {/* Tableau détaillé des rendez-vous par médecin */}
              {selectedMedecinForDetail && (
                <div className="mt-4">
                  <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">
                        Détails des rendez-vous: Dr. {selectedMedecinForDetail.prenom} {selectedMedecinForDetail.nom}
                      </h6>
                      <div className="btn-group btn-group-sm">
                        <button className="btn btn-outline-primary active" onClick={() => getDetailsRendezvousMedecin(selectedMedecinForDetail.id)}>
                          Journalier
                        </button>
                        <button className="btn btn-outline-primary" disabled>
                          Hebdomadaire
                        </button>
                        <button className="btn btn-outline-primary" disabled>
                          Mensuel
                        </button>
                        <button className="btn btn-outline-primary" disabled>
                          Annuel
                        </button>
                      </div>
                    </div>
                    <div className="card-body">
                      {detailsRendezvousMedecin.length === 0 ? (
                        <p className="text-center">Aucun rendez-vous trouvé pour ce médecin</p>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-sm table-striped table-bordered">
                            <thead className="table-light">
                              <tr>
                                <th>Date</th>
                                <th className="text-center">Nombre de RDV</th>
                                <th className="text-end">Tarif unitaire</th>
                                <th className="text-end">Revenu total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {detailsRendezvousMedecin.map(detail => {
                                const revenuJour = calculerRevenuPourDate(detail.dateStr, selectedMedecinForDetail.tarif);
                                return (
                                  <tr key={detail.dateStr}>
                                    <td>{formatDate(detail.dateStr)}</td>
                                    <td className="text-center">{detail.count}</td>
                                    <td className="text-end">{parseInt(selectedMedecinForDetail.tarif).toLocaleString()} FCFA</td>
                                    <td className="text-end fw-bold">{revenuJour.toLocaleString()} FCFA</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            <tfoot className="table-secondary">
                              <tr>
                                <td className="fw-bold">Total</td>
                                <td className="text-center fw-bold">
                                  {detailsRendezvousMedecin.reduce((sum, detail) => sum + detail.count, 0)}
                                </td>
                                <td className="text-end"></td>
                                <td className="text-end fw-bold">
                                  {detailsRendezvousMedecin
                                    .reduce((sum, detail) => sum + calculerRevenuPourDate(detail.dateStr, selectedMedecinForDetail.tarif), 0)
                                    .toLocaleString()} FCFA
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // Ajouter une section pour les statistiques comparatives des médecins
  const renderStatistiquesComparatives = () => {
    // Calculer les statistiques
    const stats = getRendezvousTotalParPeriode(medecinRendezvous);
    
    // Données pour les graphiques
    const rdvData = stats.statsMedecins.map(stat => ({
      name: stat.nom.substring(0, 15) + (stat.nom.length > 15 ? '...' : ''),
      rdv: stat.nbRendezvous,
      revenu: stat.revenu
    }));
    
    return (
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">
            <FaChartBar className="me-2" />
            Statistiques comparatives des médecins
          </h5>
        </div>
        <div className="card-body">
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="card border-primary">
                <div className="card-header bg-primary text-white">
                  Nombre total de rendez-vous
                </div>
                <div className="card-body text-center">
                  <h3 className="mb-0">{stats.totalRdv}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-success">
                <div className="card-header bg-success text-white">
                  Revenus potentiels totaux
                </div>
                <div className="card-body text-center">
                  <h3 className="mb-0">{stats.revenusTotal.toLocaleString()} FCFA</h3>
                </div>
              </div>
            </div>
          </div>
          
          {/* Graphiques */}
          <div className="row">
            <div className="col-md-6">
              <h6 className="text-center mb-3">Répartition des rendez-vous par médecin</h6>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={rdvData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="rdv" fill="#3498db" name="Rendez-vous" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="col-md-6">
              <h6 className="text-center mb-3">Répartition des revenus par médecin</h6>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={rdvData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${value.toLocaleString()} FCFA`, 'Revenu']}/>
                  <Bar dataKey="revenu" fill="#27ae60" name="Revenu (FCFA)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          {/* Tableau comparatif */}
          <div className="table-responsive mt-4">
            <table className="table table-bordered table-hover">
              <thead className="table-primary">
                <tr>
                  <th>Médecin</th>
                  <th>Spécialité</th>
                  <th className="text-center">Rendez-vous</th>
                  <th className="text-end">% RDV</th>
                  <th className="text-end">Tarif (FCFA)</th>
                  <th className="text-end">Revenu (FCFA)</th>
                  <th className="text-end">% Revenu</th>
                </tr>
              </thead>
              <tbody>
                {stats.statsMedecins.map(stat => (
                  <tr key={stat.id}>
                    <td>{stat.nom}</td>
                    <td>{stat.specialite}</td>
                    <td className="text-center">{stat.nbRendezvous}</td>
                    <td className="text-end">{stat.pourcentageRdv.toFixed(1)}%</td>
                    <td className="text-end">{parseInt(stat.tarif).toLocaleString()}</td>
                    <td className="text-end">{stat.revenu.toLocaleString()}</td>
                    <td className="text-end">{stat.pourcentageRevenu.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="table-secondary">
                <tr>
                  <td colSpan="2" className="fw-bold">Total</td>
                  <td className="text-center fw-bold">{stats.totalRdv}</td>
                  <td className="text-end fw-bold">100%</td>
                  <td className="text-end"></td>
                  <td className="text-end fw-bold">{stats.revenusTotal.toLocaleString()}</td>
                  <td className="text-end fw-bold">100%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Fonction pour réinitialiser les calculs pour un seul médecin
  const reinitialiserCalculsMedecin = (medecinId) => {
    if (window.confirm(`Êtes-vous sûr de vouloir réinitialiser tous les calculs pour ce médecin ? Cette action est irréversible.`)) {
      // Mettre à jour l'historique local en supprimant les entrées de ce médecin
      const nouvelHistorique = {...historiqueRevenus};
      
      // Parcourir chaque type de période et supprimer les entrées du médecin
      Object.keys(nouvelHistorique).forEach(typePeriode => {
        Object.keys(nouvelHistorique[typePeriode]).forEach(periode => {
          if (nouvelHistorique[typePeriode][periode][medecinId]) {
            delete nouvelHistorique[typePeriode][periode][medecinId];
          }
        });
      });
      
      setHistoriqueRevenus(nouvelHistorique);
      
      // Sauvegarder l'historique mis à jour dans localStorage
      try {
        localStorage.setItem(`historique_revenus_${admin?.structureId}`, JSON.stringify(nouvelHistorique));
      } catch (error) {
        console.error("Erreur lors de la sauvegarde de l'historique:", error);
      }
      
      // Recalculer les revenus avec l'historique mis à jour
      const medecinsAvecRevenus = calculerRevenusMedecins(medecinRendezvous, periodeMedecinsRdv);
      setMedecinRendezvous(medecinsAvecRevenus);
      
      toast.success(`Les calculs pour ce médecin ont été réinitialisés`);
    }
  };

  // Fonction pour réinitialiser les calculs pour tous les médecins
  const reinitialiserCalculsTousMedecins = () => {
    if (window.confirm("Êtes-vous sûr de vouloir réinitialiser tous les calculs pour TOUS les médecins ? Cette action est irréversible.")) {
      // Réinitialiser tout l'historique
      const nouvelHistorique = { jour: {}, semaine: {}, mois: {}, annee: {} };
      
      setHistoriqueRevenus(nouvelHistorique);
      
      // Sauvegarder l'historique vide dans localStorage
      try {
        localStorage.setItem(`historique_revenus_${admin?.structureId}`, JSON.stringify(nouvelHistorique));
      } catch (error) {
        console.error("Erreur lors de la sauvegarde de l'historique:", error);
      }
      
      // Recalculer les revenus avec l'historique vide
      const medecinsAvecRevenus = calculerRevenusMedecins(medecinRendezvous, periodeMedecinsRdv);
      setMedecinRendezvous(medecinsAvecRevenus);
      
      toast.success("Tous les calculs ont été réinitialisés pour tous les médecins");
    }
  };

  // Créer une version mobile spécifique du rendu des médecins et rendez-vous
  const renderMobileRendezvous = () => {
    // Calculer les statistiques
    const stats = getRendezvousTotalParPeriode(medecinRendezvous);

    return (
      <div className="ms-mobile-section">
        <div className="ms-mobile-header">
          <h5 className="ms-mobile-title">
            <FaUserMd className="ms-mobile-icon" />
            Médecins et RDV
          </h5>
          <button
            className="ms-mobile-reset-button"
            onClick={reinitialiserCalculsTousMedecins}
          >
            <i className="fas fa-trash-alt"></i> Réinitialiser tout
          </button>
        </div>

        {/* Barre de recherche mobile */}
        <div className="ms-mobile-search">
          <div className="ms-mobile-search-box">
            <input
              type="text"
              className="ms-mobile-search-input"
              placeholder="Rechercher un médecin..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button 
                className="ms-mobile-search-clear"
                onClick={() => setSearchTerm('')}
              >
                ×
              </button>
            )}
          </div>
        </div>
        
        {medecinRendezvous.length === 0 ? (
          <div className="ms-mobile-empty">
            <p>Aucun médecin trouvé</p>
          </div>
        ) : (
          <>
            <div className="ms-mobile-stats">
              <div className="ms-mobile-stat-card">
                <div className="ms-mobile-stat-label">Total RDV</div>
                <div className="ms-mobile-stat-value">{stats.totalRdv}</div>
              </div>
              <div className="ms-mobile-stat-card">
                <div className="ms-mobile-stat-label">Revenus potentiels</div>
                <div className="ms-mobile-stat-value">{stats.revenusTotal.toLocaleString()} FCFA</div>
              </div>
            </div>
            
            <div className="ms-mobile-medecins">
              {medecinRendezvous
                .filter(medecin => {
                  if (!searchTerm) return true;
                  const searchTermLower = searchTerm.toLowerCase();
                  return (
                    medecin.nom.toLowerCase().includes(searchTermLower) ||
                    medecin.prenom.toLowerCase().includes(searchTermLower) ||
                    medecin.specialite.toLowerCase().includes(searchTermLower)
                  );
                })
                .map(medecin => (
                <div 
                  key={medecin.id} 
                  className={`ms-mobile-medecin-card ${selectedMedecinForDetail?.id === medecin.id ? 'ms-mobile-selected' : ''}`}
                >
                  <div className="ms-mobile-medecin-header">
                    <div className="ms-mobile-medecin-avatar">
                      {medecin.prenom?.charAt(0)}{medecin.nom?.charAt(0)}
                    </div>
                    <div className="ms-mobile-medecin-info">
                      <div className="ms-mobile-medecin-name">Dr. {medecin.prenom} {medecin.nom}</div>
                      <div className="ms-mobile-medecin-specialite">{medecin.specialite}</div>
                    </div>
                    <div className="ms-mobile-medecin-rdv">
                      <span className="ms-mobile-rdv-badge">{medecin.nbRendezvous}</span> RDV
                    </div>
                  </div>
                  
                  <div className="ms-mobile-medecin-tarif">
                    <div className="ms-mobile-tarif-label">Tarif (FCFA)</div>
                    <div className="ms-mobile-tarif-input">
                      <input
                        type="number"
                        value={medecin.tarif}
                        onChange={(e) => {
                          const newMedecins = [...medecinRendezvous];
                          const index = newMedecins.findIndex(m => m.id === medecin.id);
                          newMedecins[index].tarif = e.target.value;
                          setMedecinRendezvous(newMedecins);
                        }}
                        min="0"
                      />
                      <button onClick={() => handleTarifMedecinRendezvous(medecin.id, medecin.tarif)}>
                        <i className="fas fa-save"></i>
                      </button>
                    </div>
                  </div>
                  
                  <div className="ms-mobile-medecin-actions">
                    <button 
                      className="ms-mobile-action-button details"
                      onClick={() => getDetailsRendezvousMedecin(medecin.id)}
                    >
                      <i className="fas fa-eye"></i> Détails
                    </button>
                    <button 
                      className="ms-mobile-action-button reset"
                      onClick={() => reinitialiserCalculsMedecin(medecin.id)}
                    >
                      <i className="fas fa-redo-alt"></i> Réinitialiser
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            {selectedMedecinForDetail && (
              <div className="ms-mobile-details">
                <div className="ms-mobile-details-header">
                  <h6>Détails: Dr. {selectedMedecinForDetail.prenom} {selectedMedecinForDetail.nom}</h6>
                  <div className="ms-mobile-period-tabs">
                    <button className="ms-mobile-period-tab active">Journalier</button>
                    <button className="ms-mobile-period-tab" disabled>Semaine</button>
                    <button className="ms-mobile-period-tab" disabled>Mois</button>
                    <button className="ms-mobile-period-tab" disabled>Année</button>
                  </div>
                </div>
                
                {detailsRendezvousMedecin.length === 0 ? (
                  <div className="ms-mobile-empty">
                    <p>Aucun rendez-vous trouvé</p>
                  </div>
                ) : (
                  <div className="ms-mobile-rdv-list">
                    {detailsRendezvousMedecin.map(detail => {
                      const revenuJour = calculerRevenuPourDate(detail.dateStr, selectedMedecinForDetail.tarif);
                      return (
                        <div key={detail.dateStr} className="ms-mobile-rdv-item">
                          <div className="ms-mobile-rdv-date">{formatDate(detail.dateStr)}</div>
                          <div className="ms-mobile-rdv-info">
                            <div className="ms-mobile-rdv-count">
                              <span className="ms-mobile-rdv-badge">{detail.count}</span> RDV
                            </div>
                            <div className="ms-mobile-rdv-tarif">{parseInt(selectedMedecinForDetail.tarif).toLocaleString()} FCFA</div>
                          </div>
                          <div className="ms-mobile-rdv-revenu">{revenuJour.toLocaleString()} FCFA</div>
                        </div>
                      );
                    })}
                    
                    <div className="ms-mobile-rdv-total">
                      <div className="ms-mobile-total-label">Total</div>
                      <div className="ms-mobile-total-count">
                        {detailsRendezvousMedecin.reduce((sum, detail) => sum + detail.count, 0)} RDV
                      </div>
                      <div className="ms-mobile-total-revenu">
                        {detailsRendezvousMedecin
                          .reduce((sum, detail) => sum + calculerRevenuPourDate(detail.dateStr, selectedMedecinForDetail.tarif), 0)
                          .toLocaleString()} FCFA
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Créer une version mobile des statistiques comparatives
  const renderMobileStatistiques = () => {
    const stats = getRendezvousTotalParPeriode(medecinRendezvous);
    
    // Réorganiser les données pour les graphiques mobiles
    const rdvData = stats.statsMedecins.map(stat => ({
      name: stat.nom.split(' ').slice(-1)[0], // Utiliser seulement le nom de famille pour économiser l'espace
      rdv: stat.nbRendezvous,
      revenu: stat.revenu
    }));
    
    return (
      <div className="ms-mobile-section">
        <div className="ms-mobile-header">
          <h5 className="ms-mobile-title">
            <FaChartBar className="ms-mobile-icon" />
            Statistiques
          </h5>
        </div>
        
        <div className="ms-mobile-stats">
          <div className="ms-mobile-stat-card primary">
            <div className="ms-mobile-stat-label">Total RDV</div>
            <div className="ms-mobile-stat-value">{stats.totalRdv}</div>
          </div>
          <div className="ms-mobile-stat-card success">
            <div className="ms-mobile-stat-label">Revenus totaux</div>
            <div className="ms-mobile-stat-value">{stats.revenusTotal.toLocaleString()} FCFA</div>
          </div>
        </div>
        
        <div className="ms-mobile-charts">
          <div className="ms-mobile-chart">
            <h6>Répartition des RDV</h6>
            <div className="ms-mobile-chart-container">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={rdvData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="rdv" fill="#3498db" name="Rendez-vous" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          <div className="ms-mobile-chart">
            <h6>Répartition des revenus</h6>
            <div className="ms-mobile-chart-container">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={rdvData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${value.toLocaleString()} FCFA`, 'Revenu']}/>
                  <Bar dataKey="revenu" fill="#27ae60" name="Revenu (FCFA)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        
        <div className="ms-mobile-comparison">
          <h6>Comparatif des médecins</h6>
          <div className="ms-mobile-medecins-list">
            {stats.statsMedecins.map(stat => (
              <div key={stat.id} className="ms-mobile-comparison-item">
                <div className="ms-mobile-comparison-medecin">
                  <div className="ms-mobile-avatar small">
                    {stat.nom.split(' ').slice(-1)[0].charAt(0)}
                  </div>
                  <div className="ms-mobile-comparison-info">
                    <div className="ms-mobile-comparison-name">{stat.nom}</div>
                    <div className="ms-mobile-comparison-specialite">{stat.specialite}</div>
                  </div>
                </div>
                
                <div className="ms-mobile-comparison-stats">
                  <div className="ms-mobile-comparison-rdv">
                    <div className="ms-mobile-comparison-value">{stat.nbRendezvous}</div>
                    <div className="ms-mobile-comparison-label">RDV</div>
                    <div className="ms-mobile-comparison-percentage">({stat.pourcentageRdv.toFixed(1)}%)</div>
                  </div>
                  
                  <div className="ms-mobile-comparison-revenu">
                    <div className="ms-mobile-comparison-value">{stat.revenu.toLocaleString()}</div>
                    <div className="ms-mobile-comparison-label">FCFA</div>
                    <div className="ms-mobile-comparison-percentage">({stat.pourcentageRevenu.toFixed(1)}%)</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="ms-app">
      {/* Bouton hamburger très visible */}
      {isMobile && (
        <>
          <button 
            className="ms-hamburger-button" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          
          {/* Le menu qui apparaît lorsqu'on clique */}
          <div className={`ms-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
            <div className="ms-mobile-menu-header">
              <h3>{structure?.nom || "Cabinet Médical"}</h3>
              <button onClick={() => setMobileMenuOpen(false)}>✕</button>
            </div>
            
            <div className="ms-mobile-menu-items">
              <button 
                className={`ms-mobile-menu-item ${activeTab === 'info' ? 'active' : ''}`}
                onClick={() => {setActiveTab('info'); setMobileMenuOpen(false);}}
              >
                <FaHome /> Accueil
              </button>
              
              <button 
                className={`ms-mobile-menu-item ${activeTab === 'stats' ? 'active' : ''}`}
                onClick={() => {setActiveTab('stats'); setMobileMenuOpen(false);}}
              >
                <FaChartBar /> Statistiques
              </button>
              
              <button 
                className={`ms-mobile-menu-item ${activeTab === 'medecinsRdv' ? 'active' : ''}`}
                onClick={() => {setActiveTab('medecinsRdv'); setMobileMenuOpen(false);}}
              >
                <FaUserMd /> Médecins et RDV
              </button>
              
              <button 
                className={`ms-mobile-menu-item ${activeTab === 'admin' ? 'active' : ''}`}
                onClick={() => {setActiveTab('admin'); setMobileMenuOpen(false);}}
              >
                <FaUserCog /> Mon profil
              </button>
              
              <button 
                className={`ms-mobile-menu-item ${activeTab === 'security' ? 'active' : ''}`}
                onClick={() => {setActiveTab('security'); setMobileMenuOpen(false);}}
              >
                <FaShieldAlt /> Sécurité
              </button>
            </div>
            
            <div className="ms-mobile-menu-footer">
              <button 
                className="ms-mobile-logout" 
                onClick={handleLogout}
              >
                <FaSignOutAlt /> Déconnexion
              </button>
            </div>
          </div>
          
          {/* Overlay qui s'affiche derrière le menu */}
          {mobileMenuOpen && (
            <div className="ms-overlay" onClick={() => setMobileMenuOpen(false)}></div>
          )}
        </>
      )}
      
      {/* Sidebar navigation */}
      <div className={`ms-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="ms-sidebar-header">
          <div className="ms-logo">
            <FaUserCog size={28} />
          </div>
          <div className="ms-title">
            <h5>Cabinet Admin</h5>
            <p className="small text-muted">{structure?.nom}</p>
          </div>
        </div>
        
        <div className="ms-nav">
          <button 
            className={`ms-nav-item ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
          >
            <FaHome size={20} />
            <span>Accueil</span>
          </button>
          
          <button 
            className={`ms-nav-item ${activeTab === 'admin' ? 'active' : ''}`}
            onClick={() => setActiveTab('admin')}
          >
            <FaUserCog size={20} />
            <span>Mon profil</span>
          </button>
          
          <button 
            className={`ms-nav-item ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            <FaShieldAlt size={20} />
            <span>Sécurité</span>
          </button>
          
          <button 
            className={`ms-nav-item ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            <FaChartBar size={20} />
            <span>Statistiques</span>
          </button>
          
            <button 
              className={`ms-nav-item ${activeTab === 'medecinsRdv' ? 'active' : ''}`}
              onClick={() => setActiveTab('medecinsRdv')}
            >
              <FaUserMd className="me-2" />
              Médecins et RDV
            </button>
        
        </div>
        
        <div className="ms-sidebar-footer">
          <button 
            className="ms-nav-item ms-logout"
            onClick={handleLogout}
          >
            <FaSignOutAlt size={20} />
            <span>Déconnexion</span>
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="ms-content">
      <ToastContainer 
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />

        {/* Header */}
        <div className="ms-header">
          <div className="ms-header-title">
            <h4>{admin?.prenom} {admin?.nom}</h4>
            <p className="text-muted">{admin?.email}</p>
          </div>
          <div className="ms-header-actions">
            <button className="ms-action-button">
              <FaBell size={18} />
          </button>
            <div className="ms-user-avatar">
              {admin?.prenom?.charAt(0)}{admin?.nom?.charAt(0)}
        </div>
          </div>
        </div>

        {/* Content */}
        <div className="ms-container">
          {/* Stats overview */}
          {activeTab === 'info' && (
            <>
              <h2 className="ms-section-title">Tableau de bord</h2>
              
              <div className="ms-stats-grid">
                <div className="ms-stat-card">
                  <div className="ms-stat-icon patients">
                    <FaUserInjured size={24} />
                </div>
                  <div className="ms-stat-content">
                    <h3>{stats.patients}</h3>
                    <p>Patients</p>
            </div>
          </div>
          
                <div className="ms-stat-card">
                  <div className="ms-stat-icon doctors">
                    <FaUserMd size={24} />
                </div>
                  <div className="ms-stat-content">
                    <h3>{stats.medecins}</h3>
                    <p>Médecins</p>
            </div>
          </div>
        </div>

              <div className="ms-card">
                <div className="ms-card-header">
                  <h4>Informations de la structure</h4>
                <button 
                    className="ms-edit-button"
                    onClick={() => {
                      if (editMode) {
                        handleSaveChanges();
                      } else {
                        setEditMode(true);
                      }
                    }}
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="ms-spinner"></span>
                    ) : (
                      <FaEdit size={16} />
                    )}
                    <span>{editMode ? "Enregistrer" : "Modifier"}</span>
                  </button>
                </div>

                <div className="ms-card-body">
                  <div className="ms-form-grid">
                    <div className="ms-form-group">
                      <label htmlFor="nomStructure">Nom de la structure</label>
                      <input
                        type="text"
                        id="nomStructure"
                        name="nom"
                        className="ms-input"
                        value={formData.nom}
                        onChange={handleChange}
                        disabled={!editMode}
                      />
                  </div>
                  
                    <div className="ms-form-group">
                      <label htmlFor="emailStructure">Email</label>
                      <input
                        type="email"
                        id="emailStructure"
                        name="email"
                        className="ms-input"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={true}
                      />
                  </div>
                  
                    <div className="ms-form-group">
                      <label htmlFor="telephoneStructure">Téléphone</label>
                      <input
                        type="tel"
                        id="telephoneStructure"
                        name="telephone"
                        className="ms-input"
                        value={formData.telephone}
                        onChange={handleChange}
                        disabled={!editMode}
                      />
                    </div>
                  
                    <div className="ms-form-group">
                      <label htmlFor="siteStructure">Site web</label>
                      <input
                        type="text"
                        id="siteStructure"
                        name="site"
                        className="ms-input"
                        value={formData.site}
                        onChange={handleChange}
                        disabled={!editMode}
                      />
                    </div>
                  </div>
                  
                  <div className="ms-form-group">
                    <label htmlFor="responsableStructure">Responsable</label>
                      <input
                        type="text"
                        id="responsableStructure"
                        name="responsable"
                      className="ms-input"
                        value={formData.responsable}
                        onChange={handleChange}
                        disabled={!editMode}
                      />
                </div>

                  <div className="ms-specialties">
                    <label className="ms-label">Spécialités</label>
                        {editMode ? (
                      <div className="ms-checkbox-group">
                            {["Ophtalmologie générale", "Chirurgie réfractive", "Chirurgie de la cataracte", 
                              "Rétinologie", "Glaucome", "Ophtalmologie pédiatrique", 
                              "Neuro-ophtalmologie", "Cornée et surface oculaire", "Contactologie"].map(specialite => (
                          <label className="ms-checkbox" key={specialite}>
                                <input
                                  type="checkbox"
                                  value={specialite}
                                  checked={formData.specialites.includes(specialite)}
                                  onChange={handleSpecialiteChange}
                                />
                            <span>{specialite}</span>
                                </label>
                        ))}
                            </div>
                        ) : (
                      <div className="ms-tags">
                            {Array.isArray(formData.specialites) ? formData.specialites.map((specialite, index) => (
                          <span className="ms-tag" key={index}>{specialite}</span>
                            )) : null}
                          </div>
                        )}
                  </div>
                  
                  <div className="ms-insurances">
                    <label className="ms-label">Assurances acceptées</label>
                        {editMode ? (
                      <div className="ms-checkbox-group">
                            {['CNAMGS', 'MAAB', 'Ascoma', 'Allianz', 'CNSS', 'NSIA', 'Sanlam'].map(assurance => (
                          <label className="ms-checkbox" key={assurance}>
                                <input
                                  type="checkbox"
                                  value={assurance}
                                  checked={formData.assurances.includes(assurance)}
                                  onChange={handleAssuranceChange}
                                />
                            <span>{assurance}</span>
                                </label>
                        ))}
                            </div>
                        ) : (
                      <div className="ms-tags">
                            {formData.assurances.map((assurance, index) => (
                          <span className="ms-tag blue" key={index}>{assurance}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
            </>
          )}
          
          {activeTab === 'security' && (
            <>
              <h2 className="ms-section-title">Sécurité</h2>
              
              <div className="ms-card">
                <div className="ms-card-header">
                  <h4>Réinitialisation du mot de passe</h4>
                </div>
                <div className="ms-card-body">
                  <div className="ms-alert info">
                    <div className="ms-alert-icon">
                      <FaEye size={18} />
              </div>
                    <div className="ms-alert-content">
                      Un email de réinitialisation sera envoyé à {formData.email}
                    </div>
                  </div>
                  
                  <p className="ms-description">
                    Envoyez un lien de réinitialisation de mot de passe à la structure. 
                    La structure pourra définir un nouveau mot de passe en suivant les instructions dans l'email.
                  </p>
                  
                  <button 
                    className="ms-button primary"
                    onClick={handleResetStructurePassword}
                    disabled={loading}
                  >
                    {loading ? <span className="ms-spinner"></span> : null}
                    Envoyer un lien de réinitialisation
                  </button>
                </div>
              </div>
            </>
          )}
          
          {activeTab === 'admin' && (
            <>
              <h2 className="ms-section-title">Mon profil</h2>
              
              <div className="ms-card">
                <div className="ms-card-header">
                  <h4>Informations personnelles</h4>
                  <button 
                    className="ms-edit-button"
                    onClick={() => {
                      if (editMode) {
                        handleSaveAdminChanges();
                      } else {
                        setEditMode(true);
                      }
                    }}
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="ms-spinner"></span>
                    ) : (
                      <FaEdit size={16} />
                    )}
                    <span>{editMode ? "Enregistrer" : "Modifier"}</span>
                  </button>
                </div>

                <div className="ms-card-body">
                  <div className="ms-form-grid">
                    <div className="ms-form-group">
                      <label htmlFor="nomAdmin">Nom</label>
                      <input
                        type="text"
                        id="nomAdmin"
                        name="adminNom"
                        className="ms-input"
                        value={formData.adminNom || admin?.nom || ''}
                        onChange={handleChange}
                        disabled={!editMode}
                      />
                </div>

                    <div className="ms-form-group">
                      <label htmlFor="prenomAdmin">Prénom</label>
                      <input
                        type="text"
                        id="prenomAdmin"
                        name="adminPrenom"
                        className="ms-input"
                        value={formData.adminPrenom || admin?.prenom || ''}
                        onChange={handleChange}
                        disabled={!editMode}
                      />
                    </div>
                    
                    <div className="ms-form-group">
                      <label htmlFor="emailAdmin">Email</label>
                      <input
                        type="email"
                        id="emailAdmin"
                        name="adminEmail"
                        className="ms-input"
                        value={admin?.email || ''}
                        disabled={true}
                      />
                    </div>
                    
                    <div className="ms-form-group">
                      <label htmlFor="telephoneAdmin">Téléphone</label>
                      <input
                        type="tel"
                        id="telephoneAdmin"
                        name="adminTelephone"
                        className="ms-input"
                        value={formData.adminTelephone || admin?.telephone || ''}
                        onChange={handleChange}
                        disabled={!editMode}
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="ms-card">
                <div className="ms-card-header">
                  <h4>Sécurité du compte</h4>
                </div>
                
                <div className="ms-card-body">
                  <div className="ms-password-form">
                    <div className="ms-form-group password">
                      <label htmlFor="adminCurrentPassword">Mot de passe actuel</label>
                      <div className="ms-password-field">
                      <input
                        type={passwordVisible ? "text" : "password"}
                          id="adminCurrentPassword"
                          name="adminCurrentPassword"
                          className="ms-input"
                          value={formData.adminCurrentPassword || ''}
                        onChange={handleChange}
                          disabled={!editMode}
                      />
                      <button 
                        type="button" 
                          className="ms-password-toggle"
                        onClick={() => setPasswordVisible(!passwordVisible)}
                      >
                          {passwordVisible ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                      </button>
                    </div>
                  </div>
                  
                    <div className="ms-password-row">
                      <div className="ms-form-group password">
                        <label htmlFor="adminNewPassword">Nouveau mot de passe</label>
                      <input
                        type={passwordVisible ? "text" : "password"}
                          id="adminNewPassword"
                          name="adminNewPassword"
                          className="ms-input"
                          value={formData.adminNewPassword || ''}
                        onChange={handleChange}
                          disabled={!editMode}
                      />
                  </div>
                  
                      <div className="ms-form-group password">
                        <label htmlFor="adminConfirmPassword">Confirmer le mot de passe</label>
                      <input
                        type={passwordVisible ? "text" : "password"}
                          id="adminConfirmPassword"
                          name="adminConfirmPassword"
                          className="ms-input"
                          value={formData.adminConfirmPassword || ''}
                        onChange={handleChange}
                          disabled={!editMode}
                      />
                      </div>
                    </div>
                    </div>
                  </div>
                </div>

              <div className="ms-card">
                <div className="ms-card-header">
                  <h4>Notifications</h4>
                </div>
                
                <div className="ms-card-body">
                  <div className="ms-toggles">
                    <div className="ms-toggle-item">
                      <div className="ms-toggle-info">
                        <h5>Notifications par email</h5>
                        <p>Recevoir des alertes par email</p>
                      </div>
                      <label className="ms-toggle">
                        <input
                          type="checkbox"
                          checked={formData.adminEmailNotifications || false}
                          onChange={(e) => setFormData({
                            ...formData,
                            adminEmailNotifications: e.target.checked
                          })}
                          disabled={!editMode}
                        />
                        <span className="ms-toggle-slider"></span>
                      </label>
                    </div>
                    
                    <div className="ms-toggle-item">
                      <div className="ms-toggle-info">
                        <h5>Alertes nouveaux patients</h5>
                        <p>Être notifié à chaque inscription</p>
                      </div>
                      <label className="ms-toggle">
                        <input
                          type="checkbox"
                          checked={formData.adminNewPatientAlert || false}
                          onChange={(e) => setFormData({
                            ...formData,
                            adminNewPatientAlert: e.target.checked
                          })}
                          disabled={!editMode}
                        />
                        <span className="ms-toggle-slider"></span>
                      </label>
                    </div>
                    
                    <div className="ms-toggle-item">
                      <div className="ms-toggle-info">
                        <h5>Alertes de connexion</h5>
                        <p>Être notifié des connexions à votre compte</p>
                      </div>
                      <label className="ms-toggle">
                        <input
                          type="checkbox"
                          checked={formData.adminLoginAlert || false}
                          onChange={(e) => setFormData({
                            ...formData,
                            adminLoginAlert: e.target.checked
                          })}
                          disabled={!editMode}
                        />
                        <span className="ms-toggle-slider"></span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          
          {activeTab === 'stats' && (
            <>
              <h2 className="ms-section-title">Statistiques détaillées</h2>
              
              {/* Version desktop */}
              <div className="desktop-only">
                <div className="ms-stats-grid detailed">
                  <div className="ms-stat-card large">
                    <div className="ms-stat-header">
                      <h4>Patients</h4>
                      <span className="ms-stat-number">{stats.patients}</span>
                    </div>
                    <div className="ms-stat-body">
                      <ResponsiveContainer width="100%" height={200}>
                        <AreaChart data={patientData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Area type="monotone" dataKey="count" stroke="#0078d4" fill="#e8f5ff" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  
                  <div className="ms-stat-card large">
                    <div className="ms-stat-header">
                      <h4>Médecins</h4>
                      <span className="ms-stat-number">{stats.medecins}</span>
                    </div>
                    <div className="ms-stat-body">
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={medecinData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="count" fill="#107c41" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
                {renderStatistiquesComparatives()}
              </div>
              
              {/* Version mobile */}
              {isMobile && renderMobileStatistiques()}
            </>
          )}
          
          {activeTab === 'medecinsRdv' && (
            <>
              {/* Version desktop */}
              <div className="desktop-only">
                {renderMedecinsRendezvous()}
              </div>
              
              {/* Version mobile */}
              {isMobile && renderMobileRendezvous()}
            </>
          )}
       

        </div>
      </div>
      
      {/* CSS pour le style Microsoft Store */}
      <style jsx>{`
        .ms-app {
          display: flex;
          min-height: 100vh;
          background-color: #f2f2f2;
          color: #252525;
          font-family: 'Segoe UI', sans-serif;
        }
        
        /* Sidebar */
        .ms-sidebar {
          width: 280px;
          background-color: white;
          box-shadow: 0 0 10px rgba(0,0,0,0.05);
          display: flex;
          flex-direction: column;
          position: fixed;
          left: 0;
          top: 0;
          bottom: 0;
          z-index: 10;
        }
        
        .ms-sidebar-header {
          padding: 20px;
          display: flex;
          align-items: center;
          border-bottom: 1px solid #f2f2f2;
        }
        
        .ms-logo {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          background-color: #0078d4;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin-right: 12px;
        }
        
        .ms-title h5 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
        }
        
        .ms-title p {
          margin: 0;
          font-size: 12px;
        }
        
        .ms-nav {
          flex: 1;
          padding: 20px 0;
          overflow-y: auto;
        }
        
        .ms-nav-item {
          display: flex;
          align-items: center;
          padding: 12px 20px;
          width: 100%;
          border: none;
          background-color: transparent;
          color: #252525;
          text-align: left;
          font-size: 14px;
          transition: background-color 0.2s;
          cursor: pointer;
        }
        
        .ms-nav-item:hover {
          background-color: #f5f5f5;
        }
        
        .ms-nav-item.active {
          background-color: #e6f2fc;
          color: #0078d4;
          font-weight: 500;
        }
        
        .ms-nav-item svg {
          margin-right: 12px;
        }
        
        .ms-sidebar-footer {
          padding: 20px 0;
          border-top: 1px solid #f2f2f2;
        }
        
        .ms-logout {
          color: #d83b01;
        }
        
        /* Main content */
        .ms-content {
          flex: 1;
          margin-left: 280px;
          padding: 24px;
        }
        
        .ms-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }
        
        .ms-header-title h4 {
          margin: 0;
          font-size: 24px;
          font-weight: 600;
        }
        
        .ms-header-title p {
          margin: 0;
          font-size: 14px;
        }
        
        .ms-header-actions {
          display: flex;
          align-items: center;
        }
        
        .ms-action-button {
          background-color: transparent;
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-left: 8px;
          color: #505050;
          cursor: pointer;
        }
        
        .ms-action-button:hover {
          background-color: #f5f5f5;
        }
        
        .ms-user-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background-color: #0078d4;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          margin-left: 12px;
        }
        
        .ms-container {
          max-width: 1200px;
          margin: 0 auto;
        }
        
        .ms-section-title {
          font-size: 20px;
          font-weight: 600;
          margin-bottom: 24px;
          color: #252525;
        }
        
        /* Stats */
        .ms-stats-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-bottom: 24px;
        }
        
        .ms-stat-card {
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 1px 5px rgba(0,0,0,0.04);
          padding: 20px;
          display: flex;
          align-items: center;
          transition: transform 0.2s, box-shadow 0.2s;
        }
        
        .ms-stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }
        
        .ms-stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 16px;
        }
        
        .ms-stat-icon.patients {
          background-color: #e8f5ff;
          color: #0078d4;
        }
        
        .ms-stat-icon.doctors {
          background-color: #d8f6e1;
          color: #107c41;
        }
        
        .ms-stat-content h3 {
          margin: 0;
          font-size: 24px;
          font-weight: 600;
        }
        
        .ms-stat-content p {
          margin: 0;
          color: #767676;
          font-size: 14px;
        }
        
        /* Detailed stats */
        .ms-stats-grid.detailed {
          grid-template-columns: 1fr;
          gap: 24px;
        }
        
        .ms-stat-card.large {
          display: block;
          padding: 0;
        }
        
        .ms-stat-header {
          padding: 20px;
          border-bottom: 1px solid #f2f2f2;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .ms-stat-header h4 {
          margin: 0;
          font-weight: 600;
        }
        
        .ms-stat-number {
          font-size: 24px;
          font-weight: 600;
          color: #0078d4;
        }
        
        .ms-stat-body {
          padding: 20px;
        }
        
        .ms-placeholder-chart {
          height: 200px;
          background-color: #f9f9f9;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #767676;
        }
        
        /* Cards */
        .ms-card {
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 1px 5px rgba(0,0,0,0.04);
          margin-bottom: 24px;
        }
        
        .ms-card-header {
          padding: 20px;
          border-bottom: 1px solid #f2f2f2;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .ms-card-header h4 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
        }
        
        .ms-edit-button {
          background-color: #f2f2f2;
          border: none;
          border-radius: 4px;
          padding: 8px 12px;
          display: flex;
          align-items: center;
          font-size: 14px;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .ms-edit-button:hover {
          background-color: #e0e0e0;
        }
        
        .ms-edit-button svg {
          margin-right: 8px;
        }
        
        .ms-card-body {
          padding: 20px;
        }
        
        /* Forms */
        .ms-form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-bottom: 16px;
        }
        
        .ms-form-group {
          margin-bottom: 16px;
        }
        
        .ms-form-group label {
          display: block;
          font-size: 14px;
          color: #505050;
          margin-bottom: 8px;
        }
        
        .ms-input {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #d1d1d1;
          border-radius: 4px;
          font-size: 14px;
          transition: border-color 0.2s;
        }
        
        .ms-input:focus {
          border-color: #0078d4;
          outline: none;
        }
        
        .ms-input:disabled {
          background-color: #f9f9f9;
          cursor: not-allowed;
        }
        
        /* Password fields */
        .ms-password-form {
          max-width: 600px;
        }
        
        .ms-password-field {
          position: relative;
        }
        
        .ms-password-toggle {
          position: absolute;
          top: 50%;
          right: 12px;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #767676;
          cursor: pointer;
        }
        
        .ms-password-row {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }
        
        /* Tags */
        .ms-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        
        .ms-tag {
          background-color: #e8f5ff;
          color: #0078d4;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 13px;
        }
        
        .ms-tag.blue {
          background-color: #e8f5ff;
          color: #0078d4;
        }
        
        /* Checkboxes */
        .ms-checkbox-group {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          margin-top: 8px;
        }
        
        .ms-checkbox {
          display: flex;
          align-items: center;
          cursor: pointer;
          font-size: 14px;
        }
        
        .ms-checkbox input {
          margin-right: 8px;
        }
        
        .ms-label {
          display: block;
          font-size: 14px;
          color: #505050;
          margin-bottom: 8px;
          font-weight: 500;
        }
        
        /* Toggles */
        .ms-toggles {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        
        .ms-toggle-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid #f2f2f2;
        }
        
        .ms-toggle-item:last-child {
          border-bottom: none;
        }
        
        .ms-toggle-info h5 {
          margin: 0 0 4px;
          font-size: 14px;
          font-weight: 500;
        }
        
        .ms-toggle-info p {
          margin: 0;
          font-size: 12px;
          color: #767676;
        }
        
        .ms-toggle {
          position: relative;
          display: inline-block;
          width: 44px;
          height: 24px;
        }
        
        .ms-toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        
        .ms-toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #d1d1d1;
          transition: .4s;
          border-radius: 24px;
        }
        
        .ms-toggle-slider:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: .4s;
          border-radius: 50%;
        }
        
        .ms-toggle input:checked + .ms-toggle-slider {
          background-color: #0078d4;
        }
        
        .ms-toggle input:checked + .ms-toggle-slider:before {
          transform: translateX(20px);
        }
        
        /* Alerts */
        .ms-alert {
          display: flex;
          align-items: flex-start;
          padding: 16px;
          border-radius: 8px;
          margin-bottom: 16px;
        }
        
        .ms-alert.info {
          background-color: #e8f5ff;
        }
        
        .ms-alert-icon {
          margin-right: 12px;
          color: #0078d4;
        }
        
        .ms-description {
          color: #505050;
          font-size: 14px;
          line-height: 1.5;
          margin-bottom: 20px;
        }
        
        /* Buttons */
        .ms-button {
          padding: 10px 16px;
          border-radius: 4px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          transition: background-color 0.2s;
        }
        
        .ms-button.primary {
          background-color: #0078d4;
          color: white;
        }
        
        .ms-button.primary:hover {
          background-color: #0062aa;
        }
        
        /* Spinner */
        .ms-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-right: 8px;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Styles mobiles */
        .ms-mobile-header-bar {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          height: 60px;
          background: #fff;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          padding: 0 15px;
          align-items: center;
          justify-content: space-between;
          z-index: 999;
        }

        .ms-hamburger-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .ms-hamburger {
          width: 24px;
          height: 18px;
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .ms-hamburger span {
          display: block;
          height: 3px;
          width: 100%;
          background-color: #333;
          border-radius: 3px;
          transition: all 0.3s ease;
        }

        .ms-hamburger.open span:nth-child(1) {
          transform: translateY(7.5px) rotate(45deg);
        }

        .ms-hamburger.open span:nth-child(2) {
          opacity: 0;
        }

        .ms-hamburger.open span:nth-child(3) {
          transform: translateY(-7.5px) rotate(-45deg);
        }

        .ms-mobile-logo {
          font-weight: 600;
          font-size: 18px;
        }

        .ms-mobile-user {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: #3498db;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
        }

        .ms-mobile-menu {
          position: fixed;
          top: 0;
          left: -280px;
          width: 280px;
          height: 100%;
          background: white;
          z-index: 1000;
          transition: all 0.3s ease;
          box-shadow: 3px 0 10px rgba(0,0,0,0.1);
          display: flex;
          flex-direction: column;
        }

        .ms-mobile-menu.open {
          left: 0;
        }

        .ms-mobile-menu-header {
          padding: 20px;
          border-bottom: 1px solid #eee;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }

        .ms-mobile-user-info {
          display: flex;
          align-items: center;
        }

        .ms-mobile-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: #3498db;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 18px;
          margin-right: 12px;
        }

        .ms-mobile-user-details h4 {
          margin: 0;
          font-size: 16px;
        }

        .ms-mobile-user-details p {
          margin: 5px 0 0;
          font-size: 12px;
          color: #777;
        }

        .ms-mobile-close {
          background: none;
          border: none;
          font-size: 24px;
          color: #333;
          cursor: pointer;
        }

        .ms-mobile-menu-items {
          flex: 1;
          overflow-y: auto;
          padding: 15px 0;
        }

        .ms-mobile-menu-item {
          display: flex;
          align-items: center;
          padding: 15px 20px;
          border: none;
          background: none;
          width: 100%;
          text-align: left;
          cursor: pointer;
          transition: background-color 0.2s;
          color: #333;
        }

        .ms-mobile-menu-item.active {
          background-color: rgba(52, 152, 219, 0.1);
          color: #3498db;
          border-left: 4px solid #3498db;
        }

        .ms-mobile-menu-item:hover {
          background-color: rgba(0, 0, 0, 0.05);
        }

        .ms-mobile-menu-item svg {
          margin-right: 15px;
        }

        .ms-mobile-menu-footer {
          padding: 15px 20px;
          border-top: 1px solid #eee;
        }

        .ms-mobile-logout {
          display: flex;
          align-items: center;
          width: 100%;
          padding: 12px 0;
          background: none;
          border: none;
          color: #e74c3c;
          font-weight: 500;
          cursor: pointer;
        }

        .ms-mobile-logout svg {
          margin-right: 12px;
        }

        .ms-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 998;
        }

        @media (max-width: 768px) {
          .ms-mobile-header-bar {
            display: flex;
          }
          
          .ms-content {
            padding-top: 70px;
          }
          
          .ms-header {
            display: none;
          }
          
          .ms-sidebar {
            display: none;
          }
        }

        /* Media Queries pour le responsive */
        @media (max-width: 991px) {
          .ms-sidebar {
            transform: translateX(-100%);
            transition: transform 0.3s ease;
            position: fixed;
            z-index: 1000;
          }
          
          .ms-sidebar.open {
            transform: translateX(0);
          }
          
          .ms-content {
            margin-left: 0;
            padding: 16px;
            padding-bottom: 80px; /* Espace pour la barre de navigation mobile */
          }
          
          .ms-mobile-menu-button {
            display: block;
          }
          
          .ms-mobile-overlay {
            display: block;
          }
          
          .ms-mobile-nav {
            display: flex;
          }
          
          .ms-header {
            padding-left: 40px; /* Pour éviter que le titre ne se superpose au bouton menu */
          }
          
          .ms-stats-grid {
            grid-template-columns: 1fr;
          }
          
          .ms-form-grid {
            grid-template-columns: 1fr;
          }
          
          .ms-checkbox-group {
            grid-template-columns: 1fr;
          }
          
          .ms-password-row {
            grid-template-columns: 1fr;
          }
          
          .ms-section-title {
            font-size: 18px;
            margin-bottom: 16px;
          }
          
          .ms-card {
            margin-bottom: 16px;
          }
          
          .ms-card-header {
            padding: 16px;
          }
          
          .ms-card-body {
            padding: 16px;
          }
        }
        
        @media (min-width: 992px) {
          .ms-mobile-menu-button,
          .ms-mobile-overlay,
          .ms-mobile-nav {
            display: none !important;
          }
        }

        /* Styles pour la gestion financière */
        .ms-tarifs-grid {
          display: flex;
          flex-direction: column;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          overflow: hidden;
        }
        
        .ms-tarifs-header {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr;
          background-color: #f5f5f5;
          padding: 12px 16px;
          font-weight: 600;
          border-bottom: 1px solid #e0e0e0;
        }
        
        .ms-tarifs-row {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr;
          padding: 12px 16px;
          border-bottom: 1px solid #e0e0e0;
          align-items: center;
        }
        
        .ms-tarifs-row:last-child {
          border-bottom: none;
        }
        
        .ms-tarif-input {
          width: 100px;
        }
        
        .ms-button.small {
          padding: 6px 12px;
          font-size: 12px;
        }
        
        .ms-revenus-summary {
          margin-bottom: 24px;
          text-align: center;
        }
        
        .ms-revenus-total h3 {
          font-size: 32px;
          font-weight: 600;
          color: #0078d4;
          margin: 0;
        }
        
        .ms-revenus-total p {
          margin: 0;
          color: #505050;
        }
        
        .ms-periode-selector {
          display: flex;
          gap: 8px;
        }
        
        .ms-periode-btn {
          background-color: #f2f2f2;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .ms-periode-btn.active {
          background-color: #0078d4;
          color: white;
        }
        
        .ms-revenus-graph {
          margin-top: 24px;
        }
        
        @media (max-width: 768px) {
          .ms-tarifs-header, .ms-tarifs-row {
            grid-template-columns: 1fr 1fr;
            gap: 12px;
          }
          
          .ms-tarifs-header div:nth-child(2),
          .ms-tarifs-row div:nth-child(2) {
            display: none;
          }
          
          .ms-periode-selector {
            display: grid;
            grid-template-columns: 1fr 1fr;
            width: 100%;
          }
        }

        /* Styles pour la recherche */
        .ms-search-box {
          position: relative;
          display: flex;
          align-items: center;
          width: 300px;
        }

        .ms-search-input {
          padding: 8px 12px;
          padding-right: 36px;
          border: 1px solid #d1d1d1;
          border-radius: 4px;
          font-size: 14px;
          width: 100%;
          transition: all 0.2s;
        }

        .ms-search-input:focus {
          border-color: #0078d4;
          box-shadow: 0 0 0 2px rgba(0, 120, 212, 0.2);
          outline: none;
        }

        .ms-search-clear {
          position: absolute;
          right: 10px;
          background: none;
          border: none;
          font-size: 18px;
          color: #666;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          border-radius: 50%;
        }

        .ms-search-clear:hover {
          background-color: #f0f0f0;
        }

        @media (max-width: 768px) {
          .ms-search-box {
            width: 100%;
            max-width: 300px;
          }
        }

        /* Styles pour la pagination */
        .ms-pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-top: 20px;
          gap: 12px;
        }

        .ms-pagination-btn {
          background-color: #f2f2f2;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .ms-pagination-btn:hover:not(:disabled) {
          background-color: #e0e0e0;
        }

        .ms-pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .ms-pagination-info {
          font-size: 14px;
          color: #505050;
        }

        /* Styles pour le rapport médecin */
        .ms-medecin-selector {
          min-width: 250px;
        }

        .ms-select {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #d1d1d1;
          border-radius: 4px;
          font-size: 14px;
          background-color: white;
        }

        .ms-rapport-placeholder {
          text-align: center;
          padding: 40px 0;
          color: #767676;
        }

        .ms-loading-center {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 0;
        }

        .ms-loading-center .ms-spinner {
          width: 30px;
          height: 30px;
          border: 3px solid rgba(0, 120, 212, 0.2);
          border-top-color: #0078d4;
          margin-bottom: 16px;
        }

        .ms-rapport-header {
          margin-bottom: 24px;
        }

        .ms-rapport-info {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }

        .ms-rapport-stat {
          background-color: #f9f9f9;
          border-radius: 8px;
          padding: 16px;
          text-align: center;
        }

        .ms-rapport-stat h3 {
          margin: 0 0 4px;
          font-size: 24px;
          font-weight: 600;
          color: #0078d4;
        }

        .ms-rapport-stat p {
          margin: 0;
          color: #505050;
          font-size: 14px;
        }

        .ms-rapport-graph h5 {
          margin-top: 24px;
          margin-bottom: 16px;
          font-size: 16px;
          font-weight: 500;
        }

        .ms-rapport-header-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .ms-export-actions {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
        }

        .ms-export-actions h5 {
          margin: 0 0 8px;
          font-size: 14px;
          font-weight: 500;
        }

        .ms-export-buttons {
          display: flex;
          gap: 8px;
        }

        .ms-export-btn {
          padding: 6px 12px;
          border: none;
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .ms-export-btn.excel {
          background-color: #d8f6e1;
          color: #107c41;
        }

        .ms-export-btn.excel:hover {
          background-color: #c3ecd0;
        }

        .ms-export-btn.pdf {
          background-color: #ffebe6;
          color: #d83b01;
        }

        .ms-export-btn.pdf:hover {
          background-color: #ffddd3;
        }

        .ms-export-btn.ppt {
          background-color: #fff4e1;
          color: #ca5010;
        }

        .ms-export-btn.ppt:hover {
          background-color: #ffe8c4;
        }

        .ms-export-btn.word {
          background-color: #e3f1ff;
          color: #0078d4;
        }

        .ms-export-btn.word:hover {
          background-color: #d0e7ff;
        }

        @media (max-width: 768px) {
          .ms-rapport-info {
            grid-template-columns: 1fr;
          }

          .ms-rapport-header-actions {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          }

          .ms-export-actions {
            align-items: flex-start;
            width: 100%;
          }

          .ms-export-buttons {
            display: grid;
            grid-template-columns: 1fr 1fr;
            width: 100%;
          }
        }

        /* CSS pour la version mobile */
        .ms-mobile-section {
          background-color: #f8f9fa;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          margin-bottom: 16px;
        }
        
        .ms-mobile-header {
          background-color: #0078d7;
          color: white;
          padding: 12px 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .ms-mobile-title {
          font-size: 16px;
          margin: 0;
          display: flex;
          align-items: center;
        }
        
        .ms-mobile-icon {
          margin-right: 8px;
        }
        
        .ms-mobile-reset-button {
          background-color: #d9534f;
          color: white;
          border: none;
          padding: 6px 8px;
          border-radius: 4px;
          font-size: 12px;
        }
        
        .ms-mobile-empty {
          padding: 24px 16px;
          text-align: center;
          color: #6c757d;
        }
        
        /* Stats cards */
        .ms-mobile-stats {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          padding: 12px;
        }
        
        .ms-mobile-stat-card {
          background-color: white;
          padding: 12px;
          border-radius: 6px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          text-align: center;
        }
        
        .ms-mobile-stat-card.primary {
          border-left: 4px solid #0078d7;
        }
        
        .ms-mobile-stat-card.success {
          border-left: 4px solid #27ae60;
        }
        
        .ms-mobile-stat-label {
          font-size: 12px;
          color: #6c757d;
          margin-bottom: 4px;
        }
        
        .ms-mobile-stat-value {
          font-size: 18px;
          font-weight: bold;
          color: #333;
        }
        
        /* Médecin cards */
        .ms-mobile-medecins {
          padding: 12px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        .ms-mobile-medecin-card {
          background-color: white;
          border-radius: 6px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          padding: 12px;
          overflow: hidden;
        }
        
        .ms-mobile-selected {
          border: 2px solid #0078d7;
        }
        
        .ms-mobile-medecin-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;
        }
        
        .ms-mobile-medecin-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background-color: #0078d7;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
        }
        
        .ms-mobile-medecin-avatar.small {
          width: 30px;
          height: 30px;
          font-size: 12px;
        }
        
        .ms-mobile-medecin-info {
          flex: 1;
        }
        
        .ms-mobile-medecin-name {
          font-weight: bold;
          color: #333;
          font-size: 14px;
        }
        
        .ms-mobile-medecin-specialite {
          color: #6c757d;
          font-size: 12px;
        }
        
        .ms-mobile-medecin-rdv {
          text-align: center;
          font-size: 12px;
          color: #6c757d;
        }
        
        .ms-mobile-rdv-badge {
          background-color: #17a2b8;
          color: white;
          border-radius: 12px;
          padding: 2px 6px;
          font-size: 12px;
          font-weight: bold;
        }
        
        .ms-mobile-medecin-tarif {
          border-top: 1px solid #e9ecef;
          border-bottom: 1px solid #e9ecef;
          padding: 12px 0;
          margin-bottom: 12px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .ms-mobile-tarif-label {
          font-size: 12px;
          color: #6c757d;
        }
        
        .ms-mobile-tarif-input {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .ms-mobile-tarif-input input {
          width: 80px;
          text-align: right;
          padding: 4px 8px;
          border: 1px solid #ced4da;
          border-radius: 4px;
        }
        
        .ms-mobile-tarif-input button {
          background-color: #0078d7;
          color: white;
          border: none;
          width: 30px;
          height: 30px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .ms-mobile-medecin-actions {
          display: flex;
          gap: 8px;
        }
        
        .ms-mobile-action-button {
          flex: 1;
          padding: 8px;
          border: none;
          border-radius: 4px;
          font-size: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
        }
        
        .ms-mobile-action-button.details {
          background-color: #f8f9fa;
          color: #0078d7;
          border: 1px solid #0078d7;
        }
        
        .ms-mobile-action-button.reset {
          background-color: #f8f9fa;
          color: #d9534f;
          border: 1px solid #d9534f;
        }
        
        /* Détails RDV */
        .ms-mobile-details {
          margin-top: 16px;
          background-color: white;
          border-radius: 6px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          overflow: hidden;
        }
        
        .ms-mobile-details-header {
          padding: 12px 16px;
          border-bottom: 1px solid #e9ecef;
        }
        
        .ms-mobile-details-header h6 {
          margin: 0 0 8px 0;
          font-size: 14px;
        }
        
        .ms-mobile-period-tabs {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          padding-bottom: 8px;
        }
        
        .ms-mobile-period-tab {
          background-color: #f8f9fa;
          border: 1px solid #ced4da;
          color: #6c757d;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          white-space: nowrap;
        }
        
        .ms-mobile-period-tab.active {
          background-color: #0078d7;
          border-color: #0078d7;
          color: white;
        }
        
        .ms-mobile-rdv-list {
          padding: 12px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        .ms-mobile-rdv-item {
          border-bottom: 1px solid #e9ecef;
          padding-bottom: 12px;
        }
        
        .ms-mobile-rdv-date {
          font-weight: bold;
          font-size: 13px;
          margin-bottom: 8px;
          color: #333;
        }
        
        .ms-mobile-rdv-info {
          display: flex;
          justify-content: space-between;
          margin-bottom: 4px;
          font-size: 12px;
          color: #6c757d;
        }
        
        .ms-mobile-rdv-revenu {
          font-weight: bold;
          text-align: right;
          color: #28a745;
          font-size: 14px;
        }
        
        .ms-mobile-rdv-total {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          padding-top: 12px;
          font-weight: bold;
        }
        
        .ms-mobile-total-label {
          color: #333;
        }
        
        .ms-mobile-total-count {
          text-align: center;
        }
        
        .ms-mobile-total-revenu {
          text-align: right;
          color: #28a745;
        }
        
        /* Charts */
        .ms-mobile-charts {
          padding: 12px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        
        .ms-mobile-chart h6 {
          font-size: 14px;
          margin: 0 0 8px 0;
          text-align: center;
        }
        
        .ms-mobile-chart-container {
          background-color: white;
          border-radius: 6px;
          padding: 8px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
        }
        
        /* Comparison */
        .ms-mobile-comparison {
          padding: 12px;
        }
        
        .ms-mobile-comparison h6 {
          font-size: 14px;
          margin: 0 0 12px 0;
          text-align: center;
        }
        
        .ms-mobile-medecins-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        .ms-mobile-comparison-item {
          background-color: white;
          border-radius: 6px;
          padding: 12px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .ms-mobile-comparison-medecin {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .ms-mobile-comparison-info {
          flex: 1;
        }
        
        .ms-mobile-comparison-name {
          font-weight: bold;
          font-size: 13px;
        }
        
        .ms-mobile-comparison-specialite {
          color: #6c757d;
          font-size: 12px;
        }
        
        .ms-mobile-comparison-stats {
          display: flex;
          justify-content: space-between;
          padding-top: 8px;
          border-top: 1px dashed #e9ecef;
        }
        
        .ms-mobile-comparison-rdv,
        .ms-mobile-comparison-revenu {
          text-align: center;
        }
        
        .ms-mobile-comparison-value {
          font-weight: bold;
          font-size: 14px;
        }
        
        .ms-mobile-comparison-label {
          font-size: 12px;
          color: #6c757d;
        }
        
        .ms-mobile-comparison-percentage {
          font-size: 11px;
          color: #6c757d;
        }

        /* Affichage conditionnel basé sur la taille d'écran */
        @media (min-width: 769px) {
          .ms-mobile-section {
            display: none;
          }
        }
        
        @media (max-width: 768px) {
          /* Masquer les éléments desktop sur mobile */
          .desktop-only {
            display: none;
          }
        }

        /* Mobile Menu Styles */
        .ms-mobile-menu-toggle {
          display: none;
          position: fixed;
          top: 16px;
          left: 16px;
          z-index: 1001;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background-color: #fff;
          box-shadow: 0 2px 5px rgba(0,0,0,0.1);
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }

        .ms-hamburger {
          width: 20px;
          height: 16px;
          position: relative;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .ms-hamburger span {
          display: block;
          height: 2px;
          width: 100%;
          background-color: #333;
          border-radius: 2px;
          transition: all 0.3s ease;
        }

        .ms-hamburger.open span:nth-child(1) {
          transform: translateY(7px) rotate(45deg);
        }

        .ms-hamburger.open span:nth-child(2) {
          opacity: 0;
        }

        .ms-hamburger.open span:nth-child(3) {
          transform: translateY(-7px) rotate(-45deg);
        }

        /* Mobile navigation menu */
        .ms-mobile-nav {
          display: none;
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background-color: #fff;
          box-shadow: 0 -2px 10px rgba(0,0,0,0.1);
          z-index: 1000;
          padding: 12px 0;
          border-top-left-radius: 16px;
          border-top-right-radius: 16px;
        }

        .ms-mobile-nav-items {
          display: flex;
          justify-content: space-around;
        }

        .ms-mobile-nav-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 8px 0;
          color: #6c757d;
          transition: color 0.3s ease;
        }

        .ms-mobile-nav-item.active {
          color: #3498db;
        }

        .ms-mobile-nav-item svg {
          margin-bottom: 4px;
          font-size: 20px;
        }

        .ms-mobile-nav-item span {
          font-size: 12px;
          font-weight: 500;
        }

        @media (max-width: 768px) {
          .ms-mobile-menu-toggle {
            display: flex;
          }
          
          .ms-mobile-nav {
            display: block;
          }
          
          .ms-content {
            padding-bottom: 70px; /* To accommodate the bottom mobile nav */
          }
          
          /* Adjust sidebar behavior for mobile */
          .ms-sidebar {
            transform: translateX(-100%);
            transition: transform 0.3s ease;
            position: fixed;
            z-index: 1000;
            top: 0;
            left: 0;
            height: 100%;
          }
          
          .ms-sidebar.open {
            transform: translateX(0);
          }
          
          /* Add overlay when menu is open */
          .ms-overlay {
            display: none;
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0,0,0,0.5);
            z-index: 999;
          }
          
          .ms-overlay.open {
            display: block;
          }
        }

        .ms-hamburger-button {
          position: fixed;
          top: 5px;
          left: 300px;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background-color: #3498db;
          border: none;
          box-shadow: 0 4px 10px rgba(0,0,0,0.3);
          z-index: 1001;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 6px;
          cursor: pointer;
        }

        .ms-hamburger-button span {
          display: block;
          width: 25px;
          height: 3px;
          background-color: white;
          border-radius: 3px;
          transition: all 0.3s;
        }

        .ms-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0,0,0,0.5);
          z-index: 999;
        }

        .ms-mobile-menu {
          position: fixed;
          top: 0;
          left: -280px;
          width: 280px;
          height: 100%;
          background-color: white;
          z-index: 1000;
          transition: left 0.3s ease;
          box-shadow: 2px 0 10px rgba(0,0,0,0.2);
          display: flex;
          flex-direction: column;
        }

        .ms-mobile-menu.open {
          left: 0;
        }

        .ms-mobile-menu-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #eee;
        }

        .ms-mobile-menu-header h3 {
          margin: 0;
          font-size: 18px;
        }

        .ms-mobile-menu-header button {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
        }

        .ms-mobile-menu-items {
          flex: 1;
          overflow-y: auto;
          padding: 10px 0;
        }

        .ms-mobile-menu-item {
          display: flex;
          align-items: center;
          width: 100%;
          padding: 15px 20px;
          border: none;
          background: none;
          text-align: left;
          font-size: 16px;
          cursor: pointer;
          gap: 15px;
        }

        .ms-mobile-menu-item:hover {
          background-color: #f5f5f5;
        }

        .ms-mobile-menu-item.active {
          background-color: #e6f7ff;
          color: #3498db;
          border-left: 4px solid #3498db;
        }

        .ms-mobile-menu-footer {
          padding: 20px;
          border-top: 1px solid #eee;
        }

        .ms-mobile-logout {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 12px 15px;
          border: 1px solid #e74c3c;
          border-radius: 4px;
          background: none;
          color: #e74c3c;
          font-size: 16px;
          cursor: pointer;
        }

        .ms-mobile-logout:hover {
          background-color: #fff1f0;
        }

        /* Styles mobiles */
        .ms-mobile-section {
          background-color: #f8f9fa;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          margin-bottom: 16px;
        }

        /* Styles pour la recherche mobile */
        .ms-mobile-search {
          padding: 12px;
          background-color: #fff;
          border-bottom: 1px solid #e9ecef;
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .ms-mobile-search-box {
          position: relative;
          display: flex;
          align-items: center;
          background-color: #fff;
        }

        /* Ajuster le conteneur des médecins pour le défilement */
        .ms-mobile-medecins {
          padding: 12px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-height: calc(100vh - 200px); /* Hauteur maximale pour permettre le défilement */
          overflow-y: auto; /* Permet le défilement vertical */
        }

        /* S'assurer que les stats restent visibles */
        .ms-mobile-stats {
          position: sticky;
          top: 60px; /* Juste en dessous de la barre de recherche */
          z-index: 9;
          background-color: #f8f9fa;
          padding: 12px;
          margin-bottom: 0;
        }

        .ms-mobile-search-input {
          width: 100%;
          padding: 10px 12px;
          padding-right: 40px;
          border: 1px solid #ced4da;
          border-radius: 8px;
          font-size: 14px;
          background-color: #f8f9fa;
          transition: all 0.2s ease;
        }

        .ms-mobile-search-input:focus {
          outline: none;
          border-color: #3498db;
          background-color: #fff;
          box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
        }

        .ms-mobile-search-clear {
          position: absolute;
          right: 12px;
          background: none;
          border: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6c757d;
          font-size: 18px;
          cursor: pointer;
          padding: 0;
        }

        .ms-mobile-search-clear:hover {
          background-color: #e9ecef;
          color: #495057;
        }
      `}</style>
    </div>
  );
};

export default Admins;

